// Year 11-13 ICT Weekly Lessons - Aligned with Vanuatu National Curriculum
// Based on Official Year Planners from year11ICTCurriculum.js, year12ICTCurriculum.js, year13ICTData.js

// ==========================================
// YEAR 11 ICT WEEKLY LESSONS
// Aligned with year11ICTYearlyPlanner
// ==========================================
export const year11ICTWeeklyLessons = {
  term1: {
    name: 'Term 1',
    focus: 'Personal Computer Systems & Application Software Foundations',
    weeks: 9,
    scheme: [
      // UNIT 1: Introduction to Computer Systems (Weeks 1-4) - Strand 1, Sub-strand 1.1
      {
        week: 1,
        unit: 'Unit 1: Introduction to Computer Systems',
        topic: 'Hardware Components of a PC',
        strand: 'Strand 1: Personal Computer Systems',
        subStrand: '1.1 - Introduction to Computer Systems',
        learningOutcomes: [
          'Name and list major hardware components of a PC',
          'State and describe functions of major hardware components',
          'Identify input and output devices'
        ],
        activities: [
          'Computer lab tour and hardware identification',
          'Hardware components worksheet',
          'Group discussion on computer parts'
        ],
        resources: ['Computer lab', 'Hardware components', 'Worksheets'],
        dailyLessons: [
          {
            day: 'Monday',
            title: 'Introduction to Computers',
            duration: '60 min',
            objectives: ['Define what a computer is', 'Identify the main parts of a computer system'],
            introduction: 'Welcome to ICT! Let\'s explore what computers are and why they are important.',
            mainActivities: [
              { activity: 'Computer Definition', duration: '15 min', description: 'Define computer as an electronic device that processes data into information' },
              { activity: 'Computer Lab Tour', duration: '30 min', description: 'Walk around the lab identifying CPU, monitor, keyboard, mouse' },
              { activity: 'Parts Identification', duration: '15 min', description: 'Students label diagram of computer parts' }
            ],
            materials: ['Computer diagram worksheet', 'Lab computers'],
            assessment: 'Completed labeled diagram',
            homework: 'List 5 places where you see computers used in Vanuatu'
          },
          {
            day: 'Tuesday',
            title: 'Input Devices',
            duration: '60 min',
            objectives: ['Identify common input devices', 'Explain the function of each input device'],
            introduction: 'How do we give information to the computer? Through input devices!',
            mainActivities: [
              { activity: 'Input Device Demo', duration: '20 min', description: 'Demonstrate keyboard, mouse, scanner, microphone, webcam' },
              { activity: 'Hands-on Practice', duration: '25 min', description: 'Students practice using keyboard and mouse' },
              { activity: 'Input Device Chart', duration: '15 min', description: 'Create chart of input devices and their functions' }
            ],
            materials: ['Various input devices', 'Chart paper'],
            assessment: 'Correctly identify 5 input devices and their functions',
            homework: 'Find 3 more input devices at home or school'
          },
          {
            day: 'Wednesday',
            title: 'Output Devices',
            duration: '60 min',
            objectives: ['Identify common output devices', 'Understand how computers display results'],
            introduction: 'What does the computer show us? Output devices display results!',
            mainActivities: [
              { activity: 'Output Device Tour', duration: '20 min', description: 'Show monitor, printer, speakers, projector' },
              { activity: 'Comparing Outputs', duration: '25 min', description: 'Print a document, view on screen, play audio' },
              { activity: 'Classification Activity', duration: '15 min', description: 'Sort devices into input/output categories' }
            ],
            materials: ['Printer', 'Speakers', 'Sample documents'],
            assessment: 'Successfully classify 10 devices as input or output',
            homework: 'Draw and label 3 output devices'
          },
          {
            day: 'Thursday',
            title: 'The Central Processing Unit (CPU)',
            duration: '60 min',
            objectives: ['Understand the role of the CPU', 'Learn about processing components'],
            introduction: 'The CPU is the brain of the computer!',
            mainActivities: [
              { activity: 'CPU Explanation', duration: '20 min', description: 'Explain CPU as the brain that processes all instructions' },
              { activity: 'Inside the Computer', duration: '25 min', description: 'View opened computer case or detailed diagrams' },
              { activity: 'CPU Functions', duration: '15 min', description: 'Discuss fetch-decode-execute cycle simply' }
            ],
            materials: ['CPU diagram', 'Opened computer case or images'],
            assessment: 'Explain the function of CPU in own words',
            homework: 'Research: What CPU brands are common?'
          },
          {
            day: 'Friday',
            title: 'Memory and Storage Overview',
            duration: '60 min',
            objectives: ['Distinguish RAM from storage', 'Understand memory hierarchy'],
            introduction: 'Computers need memory to work and storage to save!',
            mainActivities: [
              { activity: 'RAM vs Storage', duration: '20 min', description: 'Explain difference: RAM is temporary, storage is permanent' },
              { activity: 'Memory Examples', duration: '20 min', description: 'Show RAM sticks, hard drives, SSDs' },
              { activity: 'Week Review', duration: '20 min', description: 'Review all hardware components covered' }
            ],
            materials: ['RAM samples', 'Storage devices'],
            assessment: 'Explain difference between RAM and storage',
            homework: 'Study for next week - Operating Systems'
          }
        ]
      },
      {
        week: 2,
        unit: 'Unit 1: Introduction to Computer Systems',
        topic: 'Operating System Basics and GUI',
        strand: 'Strand 1: Personal Computer Systems',
        subStrand: '1.1 - Introduction to Computer Systems',
        learningOutcomes: [
          'Start up a PC and login to OS using an access account',
          'Operate a PC using OS tools, utilities and software',
          'State and outline key functions of a PC OS',
          'Define and outline main features of GUI',
          'State functions of mouse and keyboard'
        ],
        activities: [
          'Login/logout practice',
          'Desktop navigation exercises',
          'GUI elements identification'
        ],
        resources: ['Computer lab', 'Login credentials', 'GUI worksheets'],
        dailyLessons: [
          {
            day: 'Monday',
            title: 'What is an Operating System?',
            duration: '60 min',
            objectives: ['Define operating system', 'Name common operating systems'],
            introduction: 'The Operating System manages everything on your computer!',
            mainActivities: [
              { activity: 'OS Definition', duration: '15 min', description: 'Explain OS as software that manages computer hardware and software' },
              { activity: 'OS Examples', duration: '25 min', description: 'Show Windows, macOS, Linux examples' },
              { activity: 'OS Functions', duration: '20 min', description: 'List key functions: manage files, run programs, control devices' }
            ],
            materials: ['OS comparison chart', 'Screenshots of different OS'],
            assessment: 'Name 3 operating systems and 3 functions of OS',
            homework: 'What OS does your school computer use?'
          },
          {
            day: 'Tuesday',
            title: 'Starting Up and Logging In',
            duration: '60 min',
            objectives: ['Properly start up a computer', 'Login using username and password'],
            introduction: 'Let\'s learn the correct way to start and access a computer.',
            mainActivities: [
              { activity: 'Boot Process', duration: '15 min', description: 'Explain what happens when computer starts' },
              { activity: 'Login Practice', duration: '30 min', description: 'Each student practices logging in with their account' },
              { activity: 'Password Security', duration: '15 min', description: 'Discuss importance of strong passwords' }
            ],
            materials: ['Student login credentials', 'Password tips handout'],
            assessment: 'Successfully login independently',
            homework: 'Practice logging in and out'
          },
          {
            day: 'Wednesday',
            title: 'The Desktop and GUI',
            duration: '60 min',
            objectives: ['Identify parts of the desktop', 'Define GUI - Graphical User Interface'],
            introduction: 'The desktop is your workspace on the computer!',
            mainActivities: [
              { activity: 'GUI Definition', duration: '15 min', description: 'GUI = Graphical User Interface - visual way to interact with computer' },
              { activity: 'Desktop Tour', duration: '25 min', description: 'Identify icons, taskbar, start menu, system tray' },
              { activity: 'Desktop Labeling', duration: '20 min', description: 'Label screenshot of desktop' }
            ],
            materials: ['Desktop screenshot worksheet', 'Computer access'],
            assessment: 'Label all parts of desktop correctly',
            homework: 'Explore the desktop at home'
          },
          {
            day: 'Thursday',
            title: 'Windows and Menus',
            duration: '60 min',
            objectives: ['Manage windows on desktop', 'Navigate menus'],
            introduction: 'Windows can be moved, resized, and arranged!',
            mainActivities: [
              { activity: 'Window Controls', duration: '20 min', description: 'Practice minimize, maximize, restore, close buttons' },
              { activity: 'Multi-Window Work', duration: '25 min', description: 'Work with multiple windows open' },
              { activity: 'Menu Navigation', duration: '15 min', description: 'Navigate File, Edit, View menus' }
            ],
            materials: ['Window management guide'],
            assessment: 'Demonstrate window management skills',
            homework: 'Practice menu navigation'
          },
          {
            day: 'Friday',
            title: 'Mouse and Keyboard Functions',
            duration: '60 min',
            objectives: ['State functions of mouse and keyboard', 'Use mouse and keyboard effectively'],
            introduction: 'Mouse and keyboard are your main tools for using a computer!',
            mainActivities: [
              { activity: 'Mouse Functions', duration: '15 min', description: 'Left-click, right-click, double-click, drag, scroll' },
              { activity: 'Keyboard Functions', duration: '20 min', description: 'Letter keys, number keys, function keys, shortcuts' },
              { activity: 'Practice Exercises', duration: '25 min', description: 'Complete mouse and keyboard exercises' }
            ],
            materials: ['Practice exercises', 'Shortcut reference'],
            assessment: 'Complete practice exercises accurately',
            homework: 'Practice keyboard shortcuts'
          }
        ]
      },
      {
        week: 3,
        unit: 'Unit 1: Introduction to Computer Systems',
        topic: 'Input/Output Devices and Storage',
        strand: 'Strand 1: Personal Computer Systems',
        subStrand: '1.1 - Introduction to Computer Systems',
        learningOutcomes: [
          'Outline different types of printers and their technology',
          'Define PC software and outline different types',
          'Outline different types of storage devices and data storage',
          'Describe the process of data storage and explain how a PC stores data'
        ],
        activities: [
          'Printer demonstration',
          'Storage device comparison',
          'Data storage exercises'
        ],
        resources: ['Printer access', 'Various storage devices', 'Comparison charts'],
        dailyLessons: [
          {
            day: 'Monday',
            title: 'Types of Printers',
            duration: '60 min',
            objectives: ['Identify different types of printers', 'Explain printer technologies'],
            introduction: 'Printers turn digital documents into physical copies!',
            mainActivities: [
              { activity: 'Printer Types', duration: '20 min', description: 'Inkjet, laser, dot matrix, thermal printers' },
              { activity: 'Technology Comparison', duration: '25 min', description: 'Compare how each printer type works' },
              { activity: 'Printer Demo', duration: '15 min', description: 'Demonstrate printing process' }
            ],
            materials: ['Printer comparison chart', 'Sample printouts'],
            assessment: 'List 3 printer types and how they work',
            homework: 'What type of printer does your school have?'
          },
          {
            day: 'Tuesday',
            title: 'Storage Devices Overview',
            duration: '60 min',
            objectives: ['Identify different storage devices', 'Compare storage capacities'],
            introduction: 'Where do we save our files? On storage devices!',
            mainActivities: [
              { activity: 'Storage Types', duration: '20 min', description: 'Hard drives, SSDs, USB drives, CDs/DVDs, cloud' },
              { activity: 'Hands-on Viewing', duration: '25 min', description: 'Pass around and examine different storage devices' },
              { activity: 'Capacity Comparison', duration: '15 min', description: 'Compare MB, GB, TB capacities' }
            ],
            materials: ['Various storage devices', 'Capacity chart'],
            assessment: 'Name 5 storage devices and their typical capacities',
            homework: 'What storage devices do you use?'
          },
          {
            day: 'Wednesday',
            title: 'How Data is Stored',
            duration: '60 min',
            objectives: ['Describe the process of data storage', 'Understand binary data'],
            introduction: 'Computers store everything as 1s and 0s!',
            mainActivities: [
              { activity: 'Binary Introduction', duration: '20 min', description: 'Data stored as binary - 1s and 0s' },
              { activity: 'Bits and Bytes', duration: '25 min', description: 'Explain: 8 bits = 1 byte, KB, MB, GB' },
              { activity: 'Storage Exercise', duration: '15 min', description: 'Calculate file sizes' }
            ],
            materials: ['Binary chart', 'File size calculator'],
            assessment: 'Convert between KB, MB, GB',
            homework: 'Check file sizes on your USB'
          },
          {
            day: 'Thursday',
            title: 'PC Software Types',
            duration: '60 min',
            objectives: ['Define PC software', 'Outline different types of software'],
            introduction: 'Software tells hardware what to do!',
            mainActivities: [
              { activity: 'Software Definition', duration: '15 min', description: 'Software = programs and instructions' },
              { activity: 'Software Types', duration: '30 min', description: 'System software vs application software' },
              { activity: 'Examples', duration: '15 min', description: 'List examples of each type' }
            ],
            materials: ['Software classification chart'],
            assessment: 'Classify 10 software examples',
            homework: 'List 5 applications you use'
          },
          {
            day: 'Friday',
            title: 'Hardware and Software Review',
            duration: '60 min',
            objectives: ['Review all hardware and software concepts', 'Prepare for assessment'],
            introduction: 'Let\'s review everything we\'ve learned about computers!',
            mainActivities: [
              { activity: 'Hardware Review', duration: '20 min', description: 'Review all hardware components' },
              { activity: 'Software Review', duration: '15 min', description: 'Review software types' },
              { activity: 'Practice Quiz', duration: '25 min', description: 'Complete practice quiz' }
            ],
            materials: ['Review worksheet', 'Practice quiz'],
            assessment: 'Complete practice quiz',
            homework: 'Study for Unit 1 assessment'
          }
        ]
      },
      {
        week: 4,
        unit: 'Unit 1: Introduction to Computer Systems',
        topic: 'File Management with OS',
        strand: 'Strand 1: Personal Computer Systems',
        subStrand: '1.1 - Introduction to Computer Systems',
        learningOutcomes: [
          'Use OS file manager to manage and locate PC files',
          'Create, rename, copy, move, and delete files and folders',
          'Organize files in a logical structure'
        ],
        activities: [
          'File Explorer navigation',
          'Folder organization exercise',
          'File operations practice'
        ],
        resources: ['Computer lab', 'Practice files'],
        dailyLessons: [
          {
            day: 'Monday',
            title: 'Introduction to File Explorer',
            duration: '60 min',
            objectives: ['Open and navigate File Explorer', 'Understand file paths'],
            introduction: 'File Explorer helps us find and organize our files!',
            mainActivities: [
              { activity: 'Opening File Explorer', duration: '15 min', description: 'Different ways to open File Explorer' },
              { activity: 'Navigation Practice', duration: '30 min', description: 'Navigate through Documents, Pictures, Downloads' },
              { activity: 'File Paths', duration: '15 min', description: 'Understand folder paths like C:\\Users\\Documents' }
            ],
            materials: ['File Explorer guide'],
            assessment: 'Navigate to specific folders',
            homework: 'Explore folders on school computer'
          },
          {
            day: 'Tuesday',
            title: 'Creating Files and Folders',
            duration: '60 min',
            objectives: ['Create new folders', 'Save files to specific locations'],
            introduction: 'Organization starts with good folder structure!',
            mainActivities: [
              { activity: 'Folder Creation', duration: '20 min', description: 'Create folders for ICT work' },
              { activity: 'File Saving', duration: '25 min', description: 'Save files in correct locations' },
              { activity: 'Organization Plan', duration: '15 min', description: 'Create folder structure for all subjects' }
            ],
            materials: ['Folder structure template'],
            assessment: 'Create proper folder structure',
            homework: 'Plan folder structure for personal files'
          },
          {
            day: 'Wednesday',
            title: 'Copy, Move, and Rename',
            duration: '60 min',
            objectives: ['Copy files between locations', 'Move and rename files'],
            introduction: 'Learn to copy, move, and rename files efficiently!',
            mainActivities: [
              { activity: 'Copy Practice', duration: '20 min', description: 'Copy files using right-click and Ctrl+C' },
              { activity: 'Move Practice', duration: '20 min', description: 'Move files using drag and Ctrl+X' },
              { activity: 'Rename Practice', duration: '20 min', description: 'Rename files with meaningful names' }
            ],
            materials: ['Practice files'],
            assessment: 'Complete file operation exercises',
            homework: 'Practice at home'
          },
          {
            day: 'Thursday',
            title: 'Deleting and Recycle Bin',
            duration: '60 min',
            objectives: ['Delete files safely', 'Restore from Recycle Bin'],
            introduction: 'Deleted files go to the Recycle Bin first!',
            mainActivities: [
              { activity: 'Safe Deletion', duration: '20 min', description: 'Delete to Recycle Bin' },
              { activity: 'Recycle Bin Recovery', duration: '25 min', description: 'Restore accidentally deleted files' },
              { activity: 'Permanent Delete', duration: '15 min', description: 'Empty Recycle Bin and Shift+Delete' }
            ],
            materials: ['Practice files for deletion'],
            assessment: 'Demonstrate delete and restore',
            homework: 'Check your Recycle Bin'
          },
          {
            day: 'Friday',
            title: 'Unit 1 Assessment',
            duration: '60 min',
            objectives: ['Search for files efficiently', 'Complete Unit 1 assessment'],
            introduction: 'Let\'s test our knowledge of computer systems!',
            mainActivities: [
              { activity: 'Search Function', duration: '15 min', description: 'Use search bar to find files' },
              { activity: 'Unit 1 Assessment', duration: '40 min', description: 'Complete assessment on computer systems' },
              { activity: 'Review', duration: '5 min', description: 'Discuss assessment' }
            ],
            materials: ['Assessment papers'],
            assessment: 'Unit 1 Assessment',
            homework: 'Study for History of Computers'
          }
        ]
      },
      // UNIT 2: History of Computers (Week 5) - Strand 1, Sub-strand 1.2
      {
        week: 5,
        unit: 'Unit 2: History of Computers',
        topic: 'Computer Inventors and Development',
        strand: 'Strand 1: Personal Computer Systems',
        subStrand: '1.2 - History of Computers',
        learningOutcomes: [
          'Name computer inventors in the early days',
          'List names of people involved with the invention of computers',
          'Outline major contributions by computer inventors',
          'Outline key inventions in the development of modern PCs',
          'Explain importance of early computer inventions to development of PCs',
          'Discuss importance of early inventions to development of the Internet'
        ],
        activities: [
          'Timeline creation',
          'Research presentations',
          'Video documentaries'
        ],
        resources: ['History worksheets', 'Internet for research', 'Timeline materials'],
        dailyLessons: [
          {
            day: 'Monday',
            title: 'Early Computing Devices',
            duration: '60 min',
            objectives: ['Learn about early calculating machines', 'Understand the need for computing'],
            introduction: 'Computing history goes back thousands of years!',
            mainActivities: [
              { activity: 'Abacus and Early Tools', duration: '15 min', description: 'The abacus - first calculating device' },
              { activity: 'Babbage\'s Engines', duration: '25 min', description: 'Charles Babbage - father of computing' },
              { activity: 'Timeline Start', duration: '20 min', description: 'Begin computer history timeline' }
            ],
            materials: ['History images', 'Timeline paper'],
            assessment: 'Add early devices to timeline',
            homework: 'Research Ada Lovelace'
          },
          {
            day: 'Tuesday',
            title: 'Computer Pioneers',
            duration: '60 min',
            objectives: ['Learn about key computer inventors', 'Understand their contributions'],
            introduction: 'Meet the people who invented modern computing!',
            mainActivities: [
              { activity: 'Ada Lovelace', duration: '15 min', description: 'First computer programmer' },
              { activity: 'Alan Turing', duration: '15 min', description: 'Father of computer science' },
              { activity: 'John von Neumann', duration: '15 min', description: 'Computer architecture pioneer' },
              { activity: 'Pioneer Profiles', duration: '15 min', description: 'Create profile cards' }
            ],
            materials: ['Pioneer biographies'],
            assessment: 'Complete profile cards',
            homework: 'Research one more pioneer'
          },
          {
            day: 'Wednesday',
            title: 'First Electronic Computers',
            duration: '60 min',
            objectives: ['Learn about ENIAC and early computers', 'Understand computer generations'],
            introduction: 'The first electronic computers were huge!',
            mainActivities: [
              { activity: 'ENIAC', duration: '20 min', description: 'First general-purpose electronic computer' },
              { activity: 'Computer Generations', duration: '25 min', description: 'Vacuum tubes, transistors, ICs, microprocessors' },
              { activity: 'Size Comparison', duration: '15 min', description: 'Compare ENIAC to modern computers' }
            ],
            materials: ['ENIAC images', 'Generation chart'],
            assessment: 'List the computer generations',
            homework: 'Find images of early computers'
          },
          {
            day: 'Thursday',
            title: 'The Personal Computer Revolution',
            duration: '60 min',
            objectives: ['Learn about the development of PCs', 'Know Apple and IBM contributions'],
            introduction: 'Computers became small enough for homes!',
            mainActivities: [
              { activity: 'Apple Computer', duration: '15 min', description: 'Steve Jobs and Steve Wozniak' },
              { activity: 'IBM PC', duration: '15 min', description: 'The standard PC' },
              { activity: 'Microsoft', duration: '15 min', description: 'Bill Gates and operating systems' },
              { activity: 'Timeline Update', duration: '15 min', description: 'Add PC era to timeline' }
            ],
            materials: ['PC history images'],
            assessment: 'Explain the PC revolution',
            homework: 'Ask parents about their first computer'
          },
          {
            day: 'Friday',
            title: 'The Internet and Modern Computing',
            duration: '60 min',
            objectives: ['Understand Internet development', 'Discuss modern computing trends'],
            introduction: 'The Internet changed everything!',
            mainActivities: [
              { activity: 'Internet History', duration: '20 min', description: 'From ARPANET to World Wide Web' },
              { activity: 'Tim Berners-Lee', duration: '15 min', description: 'Inventor of the World Wide Web' },
              { activity: 'Timeline Completion', duration: '25 min', description: 'Finish and present timelines' }
            ],
            materials: ['Internet history chart'],
            assessment: 'Present completed timeline',
            homework: 'Write: How computers changed our world'
          }
        ]
      },
      // UNIT 3: File Management & Security (Weeks 6-7) - Strand 1, Sub-strand 1.1
      {
        week: 6,
        unit: 'Unit 3: File Management & Security',
        topic: 'Data Privacy and Ethics',
        strand: 'Strand 1: Personal Computer Systems',
        subStrand: '1.1 - Introduction to Computer Systems',
        learningOutcomes: [
          'Define data access and data privacy',
          'Outline conditions that can affect PC stored data',
          'Explain ethical issues regarding data privacy',
          'Outline legal implications regarding data security',
          'List key roles and responsibilities in ICT'
        ],
        activities: [
          'Privacy discussions',
          'Security scenarios',
          'Ethical case studies'
        ],
        resources: ['Security guidelines', 'Case studies', 'Discussion materials'],
        dailyLessons: [
          {
            day: 'Monday',
            title: 'What is Data Privacy?',
            duration: '60 min',
            objectives: ['Define data privacy', 'Understand why privacy matters'],
            introduction: 'Your data is valuable and needs protection!',
            mainActivities: [
              { activity: 'Privacy Definition', duration: '15 min', description: 'Data privacy = control over personal information' },
              { activity: 'Personal Data Types', duration: '25 min', description: 'Names, addresses, photos, documents' },
              { activity: 'Privacy Importance', duration: '20 min', description: 'Why privacy matters in digital age' }
            ],
            materials: ['Privacy examples'],
            assessment: 'List types of personal data',
            homework: 'What personal data do you share online?'
          },
          {
            day: 'Tuesday',
            title: 'Threats to Data',
            duration: '60 min',
            objectives: ['Identify threats to computer data', 'Understand malware types'],
            introduction: 'Many threats can damage or steal your data!',
            mainActivities: [
              { activity: 'Threat Overview', duration: '20 min', description: 'Viruses, malware, hackers, physical damage' },
              { activity: 'Malware Types', duration: '25 min', description: 'Viruses, worms, trojans, ransomware' },
              { activity: 'Real Examples', duration: '15 min', description: 'Discuss actual malware incidents' }
            ],
            materials: ['Malware information sheets'],
            assessment: 'Explain 3 types of malware',
            homework: 'Research a famous virus'
          },
          {
            day: 'Wednesday',
            title: 'Ethical Issues in Computing',
            duration: '60 min',
            objectives: ['Explain ethical issues regarding data', 'Discuss responsible computer use'],
            introduction: 'Using computers comes with ethical responsibilities!',
            mainActivities: [
              { activity: 'Ethics Definition', duration: '15 min', description: 'Ethics = right and wrong in technology use' },
              { activity: 'Ethical Scenarios', duration: '30 min', description: 'Discuss: hacking, piracy, privacy invasion' },
              { activity: 'Responsible Use', duration: '15 min', description: 'Create responsible use guidelines' }
            ],
            materials: ['Ethical scenario cards'],
            assessment: 'Discuss ethical scenarios appropriately',
            homework: 'Write: What is responsible computer use?'
          },
          {
            day: 'Thursday',
            title: 'Legal Implications',
            duration: '60 min',
            objectives: ['Outline legal implications regarding data security', 'Understand computer crime laws'],
            introduction: 'Computer misuse can have legal consequences!',
            mainActivities: [
              { activity: 'Computer Crime', duration: '20 min', description: 'Hacking, data theft, piracy are crimes' },
              { activity: 'Copyright', duration: '20 min', description: 'Respecting intellectual property' },
              { activity: 'Legal Scenarios', duration: '20 min', description: 'Discuss legal vs illegal actions' }
            ],
            materials: ['Legal information sheets'],
            assessment: 'Identify legal vs illegal actions',
            homework: 'Research computer crime laws'
          },
          {
            day: 'Friday',
            title: 'ICT Roles and Responsibilities',
            duration: '60 min',
            objectives: ['List key roles and responsibilities in ICT', 'Understand ICT career responsibilities'],
            introduction: 'Many careers involve ICT responsibilities!',
            mainActivities: [
              { activity: 'ICT Careers', duration: '25 min', description: 'System admin, security analyst, developer, support' },
              { activity: 'Responsibilities', duration: '20 min', description: 'Data protection, security, ethical conduct' },
              { activity: 'Career Interest', duration: '15 min', description: 'Which ICT career interests you?' }
            ],
            materials: ['Career descriptions'],
            assessment: 'Describe 3 ICT roles',
            homework: 'Research an ICT career'
          }
        ]
      },
      {
        week: 7,
        unit: 'Unit 3: File Management & Security',
        topic: 'Data Protection and Security Measures',
        strand: 'Strand 1: Personal Computer Systems',
        subStrand: '1.1 - Introduction to Computer Systems',
        learningOutcomes: [
          'Outline and explain measures for protecting data or PC files',
          'Perform data protection and security activities',
          'Implement backup strategies'
        ],
        activities: [
          'Password creation',
          'Backup procedures',
          'Security software setup'
        ],
        resources: ['USB drives', 'Security software', 'Backup guides'],
        dailyLessons: [
          {
            day: 'Monday',
            title: 'Password Security',
            duration: '60 min',
            objectives: ['Create strong passwords', 'Understand password best practices'],
            introduction: 'Strong passwords are your first defense!',
            mainActivities: [
              { activity: 'Password Strength', duration: '20 min', description: 'What makes a strong password' },
              { activity: 'Password Creation', duration: '25 min', description: 'Practice creating strong passwords' },
              { activity: 'Password Rules', duration: '15 min', description: 'Create password guidelines poster' }
            ],
            materials: ['Password guidelines'],
            assessment: 'Create 3 strong passwords',
            homework: 'Check your password strength'
          },
          {
            day: 'Tuesday',
            title: 'Backup Strategies',
            duration: '60 min',
            objectives: ['Understand importance of backups', 'Create file backups'],
            introduction: 'Always have a backup of important files!',
            mainActivities: [
              { activity: 'Backup Importance', duration: '15 min', description: 'Why regular backups are essential' },
              { activity: 'Backup Methods', duration: '20 min', description: 'USB, external drive, cloud backup' },
              { activity: 'Backup Practice', duration: '25 min', description: 'Backup files to USB drive' }
            ],
            materials: ['USB drives', 'Backup procedure guide'],
            assessment: 'Successfully backup files',
            homework: 'Backup important files at home'
          },
          {
            day: 'Wednesday',
            title: 'Antivirus and Security Software',
            duration: '60 min',
            objectives: ['Understand antivirus software', 'Run security scans'],
            introduction: 'Antivirus software protects against malware!',
            mainActivities: [
              { activity: 'Antivirus Overview', duration: '20 min', description: 'How antivirus software works' },
              { activity: 'Running Scans', duration: '25 min', description: 'Perform virus scan on computer' },
              { activity: 'Update Importance', duration: '15 min', description: 'Keep antivirus updated' }
            ],
            materials: ['Antivirus software'],
            assessment: 'Run a security scan',
            homework: 'Check if home computer has antivirus'
          },
          {
            day: 'Thursday',
            title: 'Safe Internet Practices',
            duration: '60 min',
            objectives: ['Practice safe internet browsing', 'Recognize online threats'],
            introduction: 'Stay safe while browsing the internet!',
            mainActivities: [
              { activity: 'Safe Browsing', duration: '20 min', description: 'HTTPS, avoiding suspicious links' },
              { activity: 'Phishing Awareness', duration: '25 min', description: 'Recognizing fake emails and websites' },
              { activity: 'Safety Checklist', duration: '15 min', description: 'Create internet safety checklist' }
            ],
            materials: ['Phishing examples', 'Safety checklist'],
            assessment: 'Identify phishing attempts',
            homework: 'Share safety tips with family'
          },
          {
            day: 'Friday',
            title: 'Security Review and Assessment',
            duration: '60 min',
            objectives: ['Review all security concepts', 'Complete security assessment'],
            introduction: 'Let\'s review everything about data security!',
            mainActivities: [
              { activity: 'Security Review', duration: '20 min', description: 'Review all security measures' },
              { activity: 'Assessment', duration: '35 min', description: 'Complete security knowledge assessment' },
              { activity: 'Discussion', duration: '5 min', description: 'Discuss assessment' }
            ],
            materials: ['Assessment papers'],
            assessment: 'Security Assessment',
            homework: 'Study for Word Processing unit'
          }
        ]
      },
      // UNIT 4: Word Processing I (Weeks 8-9) - Strand 2, Sub-strand 2.1
      {
        week: 8,
        unit: 'Unit 4: Word Processing I',
        topic: 'Introduction to Word Processing',
        strand: 'Strand 2: Application Software',
        subStrand: '2.1 - Word Processing I',
        learningOutcomes: [
          'State the purpose of a word-processing application',
          'Outline the main use of word-processing applications',
          'Load and exit a word-processing application',
          'Use word-processing menu and toolbar',
          'Open existing and create new documents',
          'Apply editing and formatting features'
        ],
        activities: [
          'Microsoft Word introduction',
          'Document creation practice',
          'Basic formatting exercises'
        ],
        resources: ['Computer lab', 'Word processing guide'],
        dailyLessons: [
          {
            day: 'Monday',
            title: 'Introduction to Word Processing',
            duration: '60 min',
            objectives: ['State purpose of word processing', 'Open Microsoft Word'],
            introduction: 'Word processors help us create professional documents!',
            mainActivities: [
              { activity: 'Word Processing Benefits', duration: '15 min', description: 'Easy editing, formatting, professional output' },
              { activity: 'Opening Word', duration: '20 min', description: 'Different ways to open Microsoft Word' },
              { activity: 'Interface Tour', duration: '25 min', description: 'Explore Ribbon, Quick Access Toolbar' }
            ],
            materials: ['Interface diagram'],
            assessment: 'Open Word and identify 5 interface elements',
            homework: 'Explore Word at home'
          },
          {
            day: 'Tuesday',
            title: 'Creating Documents',
            duration: '60 min',
            objectives: ['Create a new document', 'Type and edit text'],
            introduction: 'Let\'s create your first Word document!',
            mainActivities: [
              { activity: 'New Document', duration: '10 min', description: 'Create blank document' },
              { activity: 'Typing Practice', duration: '30 min', description: 'Type a paragraph about yourself' },
              { activity: 'Basic Editing', duration: '20 min', description: 'Delete, backspace, insert text' }
            ],
            materials: ['Typing guide'],
            assessment: 'Create document with at least 50 words',
            homework: 'Practice typing'
          },
          {
            day: 'Wednesday',
            title: 'Saving Documents',
            duration: '60 min',
            objectives: ['Save documents properly', 'Understand Save vs Save As'],
            introduction: 'Always save your work!',
            mainActivities: [
              { activity: 'Save Command', duration: '20 min', description: 'Ctrl+S and File > Save' },
              { activity: 'Save As Practice', duration: '25 min', description: 'Save to different locations' },
              { activity: 'File Naming', duration: '15 min', description: 'Use meaningful file names' }
            ],
            materials: ['Save procedure guide'],
            assessment: 'Save document correctly',
            homework: 'Create and save 3 documents'
          },
          {
            day: 'Thursday',
            title: 'Text Formatting - Font',
            duration: '60 min',
            objectives: ['Apply editing and formatting features', 'Change font and size'],
            introduction: 'Formatting makes documents look professional!',
            mainActivities: [
              { activity: 'Basic Formatting', duration: '20 min', description: 'Bold (Ctrl+B), Italic (Ctrl+I), Underline (Ctrl+U)' },
              { activity: 'Font Changes', duration: '25 min', description: 'Change font type and size' },
              { activity: 'Formatting Practice', duration: '15 min', description: 'Format a paragraph' }
            ],
            materials: ['Formatting reference'],
            assessment: 'Apply formatting to document',
            homework: 'Create formatted document'
          },
          {
            day: 'Friday',
            title: 'Text Alignment and Lists',
            duration: '60 min',
            objectives: ['Align text', 'Create bullets and numbering'],
            introduction: 'Alignment and lists organize your content!',
            mainActivities: [
              { activity: 'Alignment', duration: '20 min', description: 'Left, center, right, justify' },
              { activity: 'Lists', duration: '25 min', description: 'Create bulleted and numbered lists' },
              { activity: 'Combined Practice', duration: '15 min', description: 'Create formatted list document' }
            ],
            materials: ['Alignment examples'],
            assessment: 'Create properly formatted list',
            homework: 'Create class schedule document'
          }
        ]
      },
      {
        week: 9,
        unit: 'Unit 4: Word Processing I',
        topic: 'Document Features and Term 1 Assessment',
        strand: 'Strand 2: Application Software',
        subStrand: '2.1 - Word Processing I',
        learningOutcomes: [
          'Create documents using specific features',
          'Produce full documents meeting specific requirements',
          'Save files and use print preview',
          'Print documents with specified properties',
          'Define word-processing fonts and name font types'
        ],
        activities: [
          'Advanced formatting',
          'Document creation project',
          'Term assessment'
        ],
        resources: ['Computer lab', 'Assessment materials'],
        dailyLessons: [
          {
            day: 'Monday',
            title: 'Copy, Cut, and Paste',
            duration: '60 min',
            objectives: ['Copy and paste text', 'Cut and move text'],
            introduction: 'Learn to move text efficiently!',
            mainActivities: [
              { activity: 'Copy and Paste', duration: '20 min', description: 'Ctrl+C, Ctrl+V' },
              { activity: 'Cut and Move', duration: '20 min', description: 'Ctrl+X for moving text' },
              { activity: 'Practice', duration: '20 min', description: 'Reorganize document' }
            ],
            materials: ['Practice document'],
            assessment: 'Reorganize document correctly',
            homework: 'Practice shortcuts'
          },
          {
            day: 'Tuesday',
            title: 'Tables and Print Preview',
            duration: '60 min',
            objectives: ['Insert and format tables', 'Use print preview'],
            introduction: 'Tables organize data, preview before printing!',
            mainActivities: [
              { activity: 'Insert Table', duration: '25 min', description: 'Create and format tables' },
              { activity: 'Print Preview', duration: '20 min', description: 'Preview document before printing' },
              { activity: 'Print Settings', duration: '15 min', description: 'Page setup and print options' }
            ],
            materials: ['Table examples'],
            assessment: 'Create formatted table',
            homework: 'Create table for timetable'
          },
          {
            day: 'Wednesday',
            title: 'Word Processing Project',
            duration: '60 min',
            objectives: ['Produce full documents meeting specific requirements', 'Apply all learned skills'],
            introduction: 'Create a complete document using all your skills!',
            mainActivities: [
              { activity: 'Project Work', duration: '50 min', description: 'Create formal letter or report' },
              { activity: 'Self-Check', duration: '10 min', description: 'Review document against criteria' }
            ],
            materials: ['Project requirements'],
            assessment: 'Complete project document',
            homework: 'Finish project if needed'
          },
          {
            day: 'Thursday',
            title: 'Term 1 Review',
            duration: '60 min',
            objectives: ['Review all Term 1 topics'],
            introduction: 'Let\'s prepare for the term assessment!',
            mainActivities: [
              { activity: 'Hardware Review', duration: '15 min', description: 'Review computer systems' },
              { activity: 'Security Review', duration: '15 min', description: 'Review data protection' },
              { activity: 'Word Processing Review', duration: '15 min', description: 'Review word processing' },
              { activity: 'Q&A', duration: '15 min', description: 'Answer questions' }
            ],
            materials: ['Review materials'],
            assessment: 'Practice questions',
            homework: 'Final study'
          },
          {
            day: 'Friday',
            title: 'Term 1 Assessment (CA Task 1)',
            duration: '60 min',
            objectives: ['Complete Term 1 Assessment'],
            introduction: 'Show what you\'ve learned this term!',
            mainActivities: [
              { activity: 'Theory Test', duration: '30 min', description: 'Written assessment' },
              { activity: 'Practical Test', duration: '25 min', description: 'Word processing practical' },
              { activity: 'Submission', duration: '5 min', description: 'Submit all work' }
            ],
            materials: ['Assessment papers'],
            assessment: 'CA Task 1 - Teacher Designed Task',
            homework: 'Enjoy term break!'
          }
        ]
      }
    ]
  },
  term2: {
    name: 'Term 2',
    focus: 'Application Software - Spreadsheets & Databases',
    weeks: 10,
    scheme: [
      // UNIT 5: Spreadsheets I (Weeks 1-5) - Strand 2, Sub-strand 2.2
      {
        week: 1,
        unit: 'Unit 5: Spreadsheets I',
        topic: 'Introduction to Spreadsheets',
        strand: 'Strand 2: Application Software',
        subStrand: '2.2 - Spreadsheets I',
        learningOutcomes: [
          'State and outline main uses of spreadsheet applications',
          'Load and exit a spreadsheet application',
          'Open existing and new spreadsheet files'
        ],
        activities: ['Excel introduction', 'Cell navigation', 'Data entry'],
        resources: ['Computer lab', 'Excel guide'],
        dailyLessons: [
          {
            day: 'Monday',
            title: 'Introduction to Spreadsheets',
            duration: '60 min',
            objectives: ['Define what a spreadsheet is', 'Identify uses of spreadsheets in daily life'],
            introduction: 'Spreadsheets are powerful tools for organizing and calculating data. Let\'s explore!',
            mainActivities: [
              { activity: 'Spreadsheet Overview', duration: '15 min', description: 'Show real-world examples: budgets, timetables, gradebooks' },
              { activity: 'Excel Tour', duration: '30 min', description: 'Open Excel, identify ribbons, tabs, cells, rows, columns' },
              { activity: 'Navigation Practice', duration: '15 min', description: 'Move around cells using arrow keys and mouse' }
            ],
            materials: ['Computer lab', 'Excel installed'],
            assessment: 'Can navigate to specific cells (e.g., go to cell D15)',
            homework: 'List 5 ways spreadsheets could help in your village or school'
          },
          {
            day: 'Tuesday',
            title: 'Cells, Rows, and Columns',
            duration: '60 min',
            objectives: ['Understand cell references', 'Enter text and numbers into cells'],
            introduction: 'Every spreadsheet is made of cells. Each cell has a unique address!',
            mainActivities: [
              { activity: 'Cell Address Demo', duration: '15 min', description: 'Explain cell references: A1, B2, etc.' },
              { activity: 'Data Entry Practice', duration: '30 min', description: 'Enter student names and ages into a simple table' },
              { activity: 'Formatting Basics', duration: '15 min', description: 'Bold headers, adjust column width' }
            ],
            materials: ['Student data worksheet'],
            assessment: 'Create a simple table with 5 names and ages',
            homework: 'Practice creating a table of your family members'
          },
          {
            day: 'Wednesday',
            title: 'Opening and Saving Files',
            duration: '60 min',
            objectives: ['Save spreadsheet files', 'Open existing spreadsheet files', 'Use Save As for different locations'],
            introduction: 'Learning to save your work is essential - you don\'t want to lose your data!',
            mainActivities: [
              { activity: 'Save Demo', duration: '15 min', description: 'Demonstrate Save, Save As, and file locations' },
              { activity: 'Save Practice', duration: '25 min', description: 'Students save their work to Documents folder' },
              { activity: 'File Management', duration: '20 min', description: 'Create folders, rename files, organize work' }
            ],
            materials: ['Computer lab'],
            assessment: 'Successfully save and reopen a spreadsheet file',
            homework: 'Draw the Save and Open icons from memory'
          },
          {
            day: 'Thursday',
            title: 'Entering Different Data Types',
            duration: '60 min',
            objectives: ['Enter text, numbers, and dates', 'Format cells for different data types'],
            introduction: 'Spreadsheets can hold different types of data - text, numbers, dates, and more!',
            mainActivities: [
              { activity: 'Data Types Demo', duration: '20 min', description: 'Show text vs numbers, date formats' },
              { activity: 'Mixed Data Entry', duration: '25 min', description: 'Create a class attendance sheet with names, dates, present/absent' },
              { activity: 'Number Formatting', duration: '15 min', description: 'Currency format, decimal places' }
            ],
            materials: ['Sample attendance template'],
            assessment: 'Create attendance sheet with proper formatting',
            homework: 'Think of 3 things that should be stored as numbers vs text'
          },
          {
            day: 'Friday',
            title: 'Week 1 Review & Practice',
            duration: '60 min',
            objectives: ['Review week\'s concepts', 'Complete independent practice task'],
            introduction: 'Let\'s review what we learned this week and practice!',
            mainActivities: [
              { activity: 'Quick Quiz', duration: '15 min', description: 'Oral questions on spreadsheet basics' },
              { activity: 'Practical Task', duration: '35 min', description: 'Create a school supplies inventory spreadsheet' },
              { activity: 'Peer Review', duration: '10 min', description: 'Share and check classmate\'s work' }
            ],
            materials: ['Task sheet'],
            assessment: 'Complete inventory spreadsheet with at least 10 items',
            homework: 'Study for Week 2 - Basic calculations'
          }
        ]
      },
      {
        week: 2,
        unit: 'Unit 5: Spreadsheets I',
        topic: 'Labels, Values, and Basic Formulas',
        strand: 'Strand 2: Application Software',
        subStrand: '2.2 - Spreadsheets I',
        learningOutcomes: [
          'Produce spreadsheet files with labels, values, and formulae',
          'Use basic spreadsheet calculations',
          'Apply basic functions using spreadsheet formulas'
        ],
        activities: ['Data entry practice', 'Formula creation', 'Basic calculations'],
        resources: ['Computer lab', 'Formula reference sheet'],
        dailyLessons: [
          {
            day: 'Monday',
            title: 'Introduction to Formulas',
            duration: '60 min',
            objectives: ['Understand what formulas are', 'Write simple addition formulas'],
            introduction: 'Formulas are the power of spreadsheets - they calculate automatically!',
            mainActivities: [
              { activity: 'Formula Demo', duration: '15 min', description: 'Show =A1+B1 and how it calculates' },
              { activity: 'Addition Practice', duration: '30 min', description: 'Add up prices in a shopping list' },
              { activity: 'Cell Reference Check', duration: '15 min', description: 'Change values and watch formula update' }
            ],
            materials: ['Shopping list template'],
            assessment: 'Create formula that adds 5 numbers',
            homework: 'Write 3 addition formulas on paper'
          },
          {
            day: 'Tuesday',
            title: 'Subtraction and Multiplication',
            duration: '60 min',
            objectives: ['Use subtraction in formulas', 'Use multiplication in formulas'],
            introduction: 'Excel can subtract and multiply just like addition - let\'s learn how!',
            mainActivities: [
              { activity: 'Operator Demo', duration: '15 min', description: 'Show - for subtract, * for multiply' },
              { activity: 'Budget Calculation', duration: '30 min', description: 'Create simple budget with income - expenses' },
              { activity: 'Quantity × Price', duration: '15 min', description: 'Calculate total costs (qty × price)' }
            ],
            materials: ['Budget template'],
            assessment: 'Complete budget with correct calculations',
            homework: 'Calculate your weekly allowance spending'
          },
          {
            day: 'Wednesday',
            title: 'Division and Order of Operations',
            duration: '60 min',
            objectives: ['Use division in formulas', 'Understand BODMAS in Excel'],
            introduction: 'Division uses / in Excel. And remember BODMAS for complex formulas!',
            mainActivities: [
              { activity: 'Division Demo', duration: '15 min', description: 'Show averages using division: =A1/B1' },
              { activity: 'Mixed Calculations', duration: '30 min', description: 'Calculate average marks: total/count' },
              { activity: 'BODMAS Practice', duration: '15 min', description: 'Practice formulas with brackets' }
            ],
            materials: ['Marks sheet template'],
            assessment: 'Calculate class average correctly',
            homework: 'Solve 3 order of operations problems'
          },
          {
            day: 'Thursday',
            title: 'The SUM Function',
            duration: '60 min',
            objectives: ['Use SUM function', 'Select ranges for SUM'],
            introduction: 'SUM is your best friend - it adds up everything in a range!',
            mainActivities: [
              { activity: 'SUM Demo', duration: '15 min', description: 'Show =SUM(A1:A10) vs adding each cell' },
              { activity: 'Sales Total', duration: '30 min', description: 'Use SUM to total monthly sales data' },
              { activity: 'Multiple SUMs', duration: '15 min', description: 'Sum different columns and rows' }
            ],
            materials: ['Sales data template'],
            assessment: 'Use SUM to add 20+ numbers correctly',
            homework: 'Write down 3 scenarios where SUM would help'
          },
          {
            day: 'Friday',
            title: 'Week 2 Formulas Test',
            duration: '60 min',
            objectives: ['Demonstrate formula skills', 'Complete practical assessment'],
            introduction: 'Time to show what you\'ve learned about formulas!',
            mainActivities: [
              { activity: 'Theory Quiz', duration: '15 min', description: 'Written quiz on formula concepts' },
              { activity: 'Practical Test', duration: '35 min', description: 'Create spreadsheet with various formulas' },
              { activity: 'Review', duration: '10 min', description: 'Go over answers together' }
            ],
            materials: ['Assessment materials'],
            assessment: 'Formulas practical test',
            homework: 'Review any concepts you found difficult'
          }
        ]
      },
      {
        week: 3,
        unit: 'Unit 5: Spreadsheets I',
        topic: 'Mathematical and Statistical Functions',
        strand: 'Strand 2: Application Software',
        subStrand: '2.2 - Spreadsheets I',
        learningOutcomes: [
          'Apply basic and complex functions using spreadsheet formulas',
          'Use SUM, AVERAGE, COUNT, MAX, MIN functions',
          'Edit spreadsheet files'
        ],
        activities: ['Function practice', 'Statistical calculations', 'Spreadsheet editing'],
        resources: ['Computer lab', 'Function guide'],
        dailyLessons: [
          {
            day: 'Monday',
            title: 'AVERAGE Function',
            duration: '60 min',
            objectives: ['Understand what average means', 'Use AVERAGE function in Excel'],
            introduction: 'Average tells us the typical value - very useful for marks and statistics!',
            mainActivities: [
              { activity: 'Average Concept', duration: '15 min', description: 'Explain average = total ÷ count' },
              { activity: 'AVERAGE Demo', duration: '15 min', description: 'Show =AVERAGE(A1:A10)' },
              { activity: 'Grade Average', duration: '30 min', description: 'Calculate student grade averages' }
            ],
            materials: ['Grade sheet template'],
            assessment: 'Calculate correct averages for 5 students',
            homework: 'Calculate your own subject averages'
          },
          {
            day: 'Tuesday',
            title: 'COUNT and COUNTA Functions',
            duration: '60 min',
            objectives: ['Use COUNT for numbers', 'Use COUNTA for any data'],
            introduction: 'COUNT functions tell us how many items we have!',
            mainActivities: [
              { activity: 'COUNT Demo', duration: '15 min', description: 'Show COUNT counts only numbers' },
              { activity: 'COUNTA Demo', duration: '15 min', description: 'Show COUNTA counts any non-empty cell' },
              { activity: 'Attendance Count', duration: '30 min', description: 'Count present students in attendance sheet' }
            ],
            materials: ['Attendance template'],
            assessment: 'Use COUNT to count students in class list',
            homework: 'Think of 3 things you might want to count'
          },
          {
            day: 'Wednesday',
            title: 'MAX and MIN Functions',
            duration: '60 min',
            objectives: ['Use MAX to find highest value', 'Use MIN to find lowest value'],
            introduction: 'Who got the highest mark? MAX and MIN tell us instantly!',
            mainActivities: [
              { activity: 'MAX Demo', duration: '15 min', description: 'Show =MAX(A1:A20) finding highest' },
              { activity: 'MIN Demo', duration: '15 min', description: 'Show =MIN(A1:A20) finding lowest' },
              { activity: 'Class Results', duration: '30 min', description: 'Find highest and lowest marks in each subject' }
            ],
            materials: ['Class results template'],
            assessment: 'Correctly identify highest and lowest in 3 columns',
            homework: 'Find highest and lowest temperatures this week'
          },
          {
            day: 'Thursday',
            title: 'Combining Multiple Functions',
            duration: '60 min',
            objectives: ['Use multiple functions in one sheet', 'Create summary statistics'],
            introduction: 'Let\'s combine all our functions to create powerful analysis!',
            mainActivities: [
              { activity: 'Summary Sheet', duration: '20 min', description: 'Create sheet with SUM, AVERAGE, COUNT, MAX, MIN' },
              { activity: 'Sales Report', duration: '30 min', description: 'Analyze sales data with all functions' },
              { activity: 'Formatting', duration: '10 min', description: 'Format results for clarity' }
            ],
            materials: ['Sales data template'],
            assessment: 'Complete summary with all 5 function types',
            homework: 'Design a summary section for a gradebook'
          },
          {
            day: 'Friday',
            title: 'Week 3 Functions Practice',
            duration: '60 min',
            objectives: ['Review all functions', 'Complete comprehensive practice'],
            introduction: 'Let\'s master these functions with lots of practice!',
            mainActivities: [
              { activity: 'Function Quiz', duration: '15 min', description: 'Match functions to their purpose' },
              { activity: 'Real Data Analysis', duration: '35 min', description: 'Analyze school enrollment data' },
              { activity: 'Peer Check', duration: '10 min', description: 'Verify classmate\'s calculations' }
            ],
            materials: ['Enrollment data'],
            assessment: 'Complete data analysis with correct functions',
            homework: 'Study for charts next week'
          }
        ]
      },
      {
        week: 4,
        unit: 'Unit 5: Spreadsheets I',
        topic: 'Data Manipulation and Charts',
        strand: 'Strand 2: Application Software',
        subStrand: '2.2 - Spreadsheets I',
        learningOutcomes: [
          'Apply data manipulation features (sorting, filtering)',
          'Identify appropriate graphs for specific data sets',
          'Produce spreadsheet graphs to represent data'
        ],
        activities: ['Sorting and filtering', 'Chart creation', 'Data visualization'],
        resources: ['Computer lab', 'Chart examples'],
        dailyLessons: [
          {
            day: 'Monday',
            title: 'Sorting Data',
            duration: '60 min',
            objectives: ['Sort data alphabetically', 'Sort data numerically'],
            introduction: 'Sorting organizes your data - alphabetically or by value!',
            mainActivities: [
              { activity: 'Sort Demo', duration: '15 min', description: 'Show A-Z and Z-A sorting' },
              { activity: 'Name Sort', duration: '20 min', description: 'Sort student list alphabetically' },
              { activity: 'Number Sort', duration: '25 min', description: 'Sort marks from highest to lowest' }
            ],
            materials: ['Student data template'],
            assessment: 'Sort data correctly both ways',
            homework: 'Think of 5 things you might sort in a spreadsheet'
          },
          {
            day: 'Tuesday',
            title: 'Filtering Data',
            duration: '60 min',
            objectives: ['Apply filters to data', 'Show only specific records'],
            introduction: 'Filters let you focus on just the data you need!',
            mainActivities: [
              { activity: 'Filter Demo', duration: '15 min', description: 'Show AutoFilter feature' },
              { activity: 'Gender Filter', duration: '20 min', description: 'Filter to show only male/female students' },
              { activity: 'Number Filter', duration: '25 min', description: 'Filter to show marks above 50' }
            ],
            materials: ['Student data template'],
            assessment: 'Apply 3 different filters correctly',
            homework: 'List scenarios where filtering would help'
          },
          {
            day: 'Wednesday',
            title: 'Introduction to Charts',
            duration: '60 min',
            objectives: ['Understand purpose of charts', 'Identify chart types'],
            introduction: 'Charts turn numbers into pictures - much easier to understand!',
            mainActivities: [
              { activity: 'Chart Types', duration: '20 min', description: 'Show bar, column, line, pie charts' },
              { activity: 'When to Use Each', duration: '25 min', description: 'Match data types to chart types' },
              { activity: 'Chart Gallery', duration: '15 min', description: 'Browse Excel chart options' }
            ],
            materials: ['Chart examples handout'],
            assessment: 'Correctly identify which chart to use for 5 scenarios',
            homework: 'Find 3 charts in newspapers or online'
          },
          {
            day: 'Thursday',
            title: 'Creating Bar and Column Charts',
            duration: '60 min',
            objectives: ['Create bar charts', 'Create column charts', 'Add chart titles'],
            introduction: 'Bar and column charts are perfect for comparing values!',
            mainActivities: [
              { activity: 'Chart Creation', duration: '20 min', description: 'Select data and insert chart' },
              { activity: 'Class Chart', duration: '25 min', description: 'Create chart of student marks' },
              { activity: 'Customization', duration: '15 min', description: 'Add title, labels, colors' }
            ],
            materials: ['Student marks data'],
            assessment: 'Create a properly formatted column chart',
            homework: 'Practice creating charts at home if possible'
          },
          {
            day: 'Friday',
            title: 'Pie Charts and Line Graphs',
            duration: '60 min',
            objectives: ['Create pie charts for percentages', 'Create line graphs for trends'],
            introduction: 'Pie shows parts of a whole, line shows change over time!',
            mainActivities: [
              { activity: 'Pie Chart', duration: '25 min', description: 'Create pie chart of budget categories' },
              { activity: 'Line Graph', duration: '25 min', description: 'Create line graph of monthly rainfall' },
              { activity: 'Chart Practice', duration: '10 min', description: 'Review and improve charts' }
            ],
            materials: ['Budget and rainfall data'],
            assessment: 'Create both chart types correctly',
            homework: 'Complete chart practice worksheet'
          }
        ]
      },
      {
        week: 5,
        unit: 'Unit 5: Spreadsheets I',
        topic: 'Complete Spreadsheets and Data Integrity',
        strand: 'Strand 2: Application Software',
        subStrand: '2.2 - Spreadsheets I',
        learningOutcomes: [
          'Produce complete spreadsheet to meet specific requirements',
          'Define and apply data integrity practices'
        ],
        activities: ['Complete spreadsheet project', 'Data validation', 'Integrity practices'],
        resources: ['Computer lab', 'Project requirements'],
        dailyLessons: [
          {
            day: 'Monday',
            title: 'Data Integrity Concepts',
            duration: '60 min',
            objectives: ['Define data integrity', 'Understand importance of accurate data'],
            introduction: 'Data integrity means our data is accurate and reliable - very important!',
            mainActivities: [
              { activity: 'Integrity Discussion', duration: '20 min', description: 'What happens with wrong data?' },
              { activity: 'Error Examples', duration: '20 min', description: 'Find errors in sample spreadsheet' },
              { activity: 'Best Practices', duration: '20 min', description: 'Learn data entry best practices' }
            ],
            materials: ['Sample spreadsheet with errors'],
            assessment: 'Identify 5 data integrity issues',
            homework: 'Think about data integrity in daily life'
          },
          {
            day: 'Tuesday',
            title: 'Data Validation',
            duration: '60 min',
            objectives: ['Apply data validation rules', 'Restrict data entry to valid values'],
            introduction: 'Validation prevents wrong data from being entered!',
            mainActivities: [
              { activity: 'Validation Demo', duration: '15 min', description: 'Show Data Validation feature' },
              { activity: 'Number Limits', duration: '20 min', description: 'Restrict marks to 0-100' },
              { activity: 'Dropdown Lists', duration: '25 min', description: 'Create dropdown for gender selection' }
            ],
            materials: ['Student data template'],
            assessment: 'Apply 3 validation rules correctly',
            homework: 'Design validation rules for a product inventory'
          },
          {
            day: 'Wednesday',
            title: 'Spreadsheet Project - Part 1',
            duration: '60 min',
            objectives: ['Plan a complete spreadsheet', 'Set up structure and headers'],
            introduction: 'Time to create a real-world spreadsheet project!',
            mainActivities: [
              { activity: 'Project Brief', duration: '10 min', description: 'Explain school canteen sales tracker' },
              { activity: 'Planning', duration: '15 min', description: 'Plan columns, rows, calculations needed' },
              { activity: 'Setup', duration: '35 min', description: 'Create headers, format structure' }
            ],
            materials: ['Project requirements'],
            assessment: 'Complete project setup with proper structure',
            homework: 'Prepare sample data for tomorrow'
          },
          {
            day: 'Thursday',
            title: 'Spreadsheet Project - Part 2',
            duration: '60 min',
            objectives: ['Add data and formulas', 'Create charts for the project'],
            introduction: 'Let\'s add the calculations and visualizations!',
            mainActivities: [
              { activity: 'Data Entry', duration: '15 min', description: 'Enter canteen sales data' },
              { activity: 'Formulas', duration: '25 min', description: 'Add SUM, AVERAGE for totals' },
              { activity: 'Charts', duration: '20 min', description: 'Create sales chart' }
            ],
            materials: ['Project from yesterday'],
            assessment: 'Project with working calculations and chart',
            homework: 'Review project for tomorrow\'s finish'
          },
          {
            day: 'Friday',
            title: 'Spreadsheet Unit Assessment',
            duration: '60 min',
            objectives: ['Complete and submit project', 'Review spreadsheet concepts'],
            introduction: 'Final day to polish and submit your spreadsheet project!',
            mainActivities: [
              { activity: 'Final Touches', duration: '20 min', description: 'Add validation, format nicely' },
              { activity: 'Project Submission', duration: '20 min', description: 'Save and submit project' },
              { activity: 'Unit Review', duration: '20 min', description: 'Review all spreadsheet concepts' }
            ],
            materials: ['Project file'],
            assessment: 'Complete spreadsheet project submission',
            homework: 'Prepare for Databases unit next week'
          }
        ]
      },
      // UNIT 6: Databases I (Weeks 6-8) - Strand 2, Sub-strand 2.3
      {
        week: 6,
        unit: 'Unit 6: Databases I',
        topic: 'Database Concepts and Table Creation',
        strand: 'Strand 2: Application Software',
        subStrand: '2.3 - Databases I',
        learningOutcomes: [
          'State main purpose for using database management application',
          'Load database management application',
          'Outline main use and key functions of database management',
          'Open existing and create new database files',
          'Create database tables with required fields'
        ],
        activities: ['Database introduction', 'Table creation', 'Field setup'],
        resources: ['Computer lab', 'Database guide'],
        dailyLessons: [
          {
            day: 'Monday',
            title: 'What is a Database?',
            duration: '60 min',
            objectives: ['Define database', 'Identify database uses'],
            introduction: 'Databases organize large amounts of related information!',
            mainActivities: [
              { activity: 'Database Concept', duration: '15 min', description: 'Compare to file cabinet system' },
              { activity: 'Real Examples', duration: '20 min', description: 'School records, hospital, bank systems' },
              { activity: 'Access Tour', duration: '25 min', description: 'Open Microsoft Access, explore interface' }
            ],
            materials: ['Computer lab', 'Access installed'],
            assessment: 'Name 5 places that use databases',
            homework: 'Find 3 more database examples'
          },
          {
            day: 'Tuesday',
            title: 'Tables, Fields, and Records',
            duration: '60 min',
            objectives: ['Understand table structure', 'Define fields and records'],
            introduction: 'Tables hold data in rows and columns - fields and records!',
            mainActivities: [
              { activity: 'Structure Demo', duration: '15 min', description: 'Show fields (columns) and records (rows)' },
              { activity: 'Table Design', duration: '30 min', description: 'Plan student database on paper' },
              { activity: 'Discussion', duration: '15 min', description: 'What fields does a student need?' }
            ],
            materials: ['Planning worksheet'],
            assessment: 'Design table with 6 appropriate fields',
            homework: 'Design a table for a village store inventory'
          },
          {
            day: 'Wednesday',
            title: 'Creating Tables in Access',
            duration: '60 min',
            objectives: ['Create a new database', 'Create a table with fields'],
            introduction: 'Let\'s create our first real database table!',
            mainActivities: [
              { activity: 'New Database', duration: '15 min', description: 'Create new database file' },
              { activity: 'Table Creation', duration: '30 min', description: 'Create Students table with fields' },
              { activity: 'Primary Key', duration: '15 min', description: 'Set StudentID as primary key' }
            ],
            materials: ['Computer lab'],
            assessment: 'Create table with correct primary key',
            homework: 'Draw your table structure'
          },
          {
            day: 'Thursday',
            title: 'Data Types in Databases',
            duration: '60 min',
            objectives: ['Choose appropriate data types', 'Set field sizes'],
            introduction: 'Each field needs the right data type - text, number, date!',
            mainActivities: [
              { activity: 'Data Types Demo', duration: '20 min', description: 'Show Text, Number, Date/Time, Yes/No' },
              { activity: 'Field Properties', duration: '25 min', description: 'Set field sizes and formats' },
              { activity: 'Validation', duration: '15 min', description: 'Add simple validation rules' }
            ],
            materials: ['Data types reference'],
            assessment: 'Assign correct data types to 8 fields',
            homework: 'List data types for village store fields'
          },
          {
            day: 'Friday',
            title: 'Week 6 Database Practice',
            duration: '60 min',
            objectives: ['Review table concepts', 'Complete table design practice'],
            introduction: 'Let\'s make sure we have table design mastered!',
            mainActivities: [
              { activity: 'Quick Quiz', duration: '15 min', description: 'Table terminology quiz' },
              { activity: 'Table Practice', duration: '35 min', description: 'Create Products table for store' },
              { activity: 'Review', duration: '10 min', description: 'Discuss common mistakes' }
            ],
            materials: ['Practice task sheet'],
            assessment: 'Complete Products table correctly',
            homework: 'Study for data entry next week'
          }
        ]
      },
      {
        week: 7,
        unit: 'Unit 6: Databases I',
        topic: 'Data Entry and Queries',
        strand: 'Strand 2: Application Software',
        subStrand: '2.3 - Databases I',
        learningOutcomes: [
          'Enter data into database tables',
          'Apply appropriate data types and field sizes',
          'Create basic and complex queries with conditions',
          'Run queries to produce required output'
        ],
        activities: ['Data entry', 'Query creation', 'Data retrieval'],
        resources: ['Computer lab', 'Query examples'],
        dailyLessons: [
          {
            day: 'Monday',
            title: 'Entering Data into Tables',
            duration: '60 min',
            objectives: ['Enter records accurately', 'Navigate between records'],
            introduction: 'Time to populate our database with real data!',
            mainActivities: [
              { activity: 'Data Entry Demo', duration: '15 min', description: 'Show datasheet view entry' },
              { activity: 'Student Data', duration: '35 min', description: 'Enter 15 student records' },
              { activity: 'Error Check', duration: '10 min', description: 'Review for entry errors' }
            ],
            materials: ['Student data list'],
            assessment: 'Enter 15 records with no errors',
            homework: 'Prepare 5 more records to enter'
          },
          {
            day: 'Tuesday',
            title: 'Introduction to Queries',
            duration: '60 min',
            objectives: ['Understand purpose of queries', 'Create simple select query'],
            introduction: 'Queries help us find exactly the data we need!',
            mainActivities: [
              { activity: 'Query Concept', duration: '15 min', description: 'Explain queries as questions to database' },
              { activity: 'Query Design', duration: '30 min', description: 'Create query to show all students' },
              { activity: 'Run Query', duration: '15 min', description: 'Execute and view query results' }
            ],
            materials: ['Student database'],
            assessment: 'Create and run simple query',
            homework: 'Think of 5 questions to ask your database'
          },
          {
            day: 'Wednesday',
            title: 'Query Criteria',
            duration: '60 min',
            objectives: ['Add criteria to queries', 'Filter query results'],
            introduction: 'Criteria narrow down your results to exactly what you need!',
            mainActivities: [
              { activity: 'Criteria Demo', duration: '15 min', description: 'Show criteria row in query design' },
              { activity: 'Text Criteria', duration: '20 min', description: 'Find students by village name' },
              { activity: 'Number Criteria', duration: '25 min', description: 'Find students with age > 15' }
            ],
            materials: ['Student database'],
            assessment: 'Create queries with 3 different criteria',
            homework: 'Write criteria for finding female students over 16'
          },
          {
            day: 'Thursday',
            title: 'Multiple Criteria and Operators',
            duration: '60 min',
            objectives: ['Use AND/OR operators', 'Create complex queries'],
            introduction: 'Combine criteria for powerful searches!',
            mainActivities: [
              { activity: 'AND Demo', duration: '15 min', description: 'Both conditions must be true' },
              { activity: 'OR Demo', duration: '15 min', description: 'Either condition can be true' },
              { activity: 'Complex Query', duration: '30 min', description: 'Find males from Santo OR females over 15' }
            ],
            materials: ['Student database'],
            assessment: 'Create query with AND and OR correctly',
            homework: 'Design 3 complex queries on paper'
          },
          {
            day: 'Friday',
            title: 'Week 7 Query Practice',
            duration: '60 min',
            objectives: ['Master query skills', 'Complete query challenges'],
            introduction: 'Query challenge day - show your skills!',
            mainActivities: [
              { activity: 'Query Quiz', duration: '10 min', description: 'Written query concepts' },
              { activity: 'Query Challenges', duration: '40 min', description: '5 query challenges to complete' },
              { activity: 'Review', duration: '10 min', description: 'Go over solutions' }
            ],
            materials: ['Challenge worksheet'],
            assessment: 'Complete at least 4 of 5 challenges',
            homework: 'Prepare for reports next week'
          }
        ]
      },
      {
        week: 8,
        unit: 'Unit 6: Databases I',
        topic: 'Reports and Forms',
        strand: 'Strand 2: Application Software',
        subStrand: '2.3 - Databases I',
        learningOutcomes: [
          'Create reports from query tables',
          'Apply calculations in queries and reports',
          'Create data entry forms'
        ],
        activities: ['Report creation', 'Form design', 'Database project'],
        resources: ['Computer lab', 'Report examples'],
        dailyLessons: [
          {
            day: 'Monday',
            title: 'Creating Database Reports',
            duration: '60 min',
            objectives: ['Understand report purpose', 'Create simple report'],
            introduction: 'Reports present your data in a printable, professional format!',
            mainActivities: [
              { activity: 'Report Demo', duration: '15 min', description: 'Show Report Wizard' },
              { activity: 'Student Report', duration: '30 min', description: 'Create student list report' },
              { activity: 'Formatting', duration: '15 min', description: 'Add title, adjust layout' }
            ],
            materials: ['Student database'],
            assessment: 'Create formatted report',
            homework: 'Design a report layout on paper'
          },
          {
            day: 'Tuesday',
            title: 'Report Calculations',
            duration: '60 min',
            objectives: ['Add totals to reports', 'Add counts and averages'],
            introduction: 'Reports can calculate summaries automatically!',
            mainActivities: [
              { activity: 'Summary Options', duration: '15 min', description: 'Show Sum, Count, Avg in reports' },
              { activity: 'Count Report', duration: '25 min', description: 'Count students by village' },
              { activity: 'Average Report', duration: '20 min', description: 'Calculate average age' }
            ],
            materials: ['Student database'],
            assessment: 'Create report with calculations',
            homework: 'Think of reports with calculations'
          },
          {
            day: 'Wednesday',
            title: 'Creating Forms',
            duration: '60 min',
            objectives: ['Understand forms purpose', 'Create data entry form'],
            introduction: 'Forms make data entry easier and more accurate!',
            mainActivities: [
              { activity: 'Form Demo', duration: '15 min', description: 'Show Form Wizard' },
              { activity: 'Student Form', duration: '30 min', description: 'Create student entry form' },
              { activity: 'Form Navigation', duration: '15 min', description: 'Move between records in form' }
            ],
            materials: ['Student database'],
            assessment: 'Create usable data entry form',
            homework: 'Enter 3 records using your form'
          },
          {
            day: 'Thursday',
            title: 'Database Project Work',
            duration: '60 min',
            objectives: ['Complete database project', 'Apply all database skills'],
            introduction: 'Time to create a complete database project!',
            mainActivities: [
              { activity: 'Project Brief', duration: '10 min', description: 'Library book database requirements' },
              { activity: 'Table Setup', duration: '20 min', description: 'Create Books table' },
              { activity: 'Forms & Queries', duration: '30 min', description: 'Create entry form and search queries' }
            ],
            materials: ['Project requirements'],
            assessment: 'Progress on database project',
            homework: 'Complete project for tomorrow'
          },
          {
            day: 'Friday',
            title: 'Term 2 Database Assessment',
            duration: '60 min',
            objectives: ['Submit database project', 'Complete Term 2 assessment'],
            introduction: 'Final assessment - show everything you\'ve learned!',
            mainActivities: [
              { activity: 'Project Finish', duration: '25 min', description: 'Final touches on project' },
              { activity: 'Submission', duration: '10 min', description: 'Submit project file' },
              { activity: 'Written Test', duration: '25 min', description: 'Database theory test' }
            ],
            materials: ['Assessment materials'],
            assessment: 'CA Task 2 - Database Assessment',
            homework: 'Enjoy term break! Prepare for programming next term'
          }
        ]
      },
      {
        week: 9,
        unit: 'Unit 6: Databases I',
        topic: 'Database Integration and Review',
        strand: 'Strand 2: Application Software',
        subStrand: '2.3 - Databases I',
        learningOutcomes: [
          'Export data from database to spreadsheet',
          'Maintain data integrity within a database',
          'Review Term 2 concepts'
        ],
        activities: ['Data export', 'Integration practice', 'Term review'],
        resources: ['Computer lab', 'Review materials'],
        dailyLessons: [
          {
            day: 'Monday',
            title: 'Exporting Data to Excel',
            duration: '60 min',
            objectives: ['Export database data', 'Work with exported data'],
            introduction: 'Sometimes we need database data in a spreadsheet!',
            mainActivities: [
              { activity: 'Export Demo', duration: '15 min', description: 'Export table to Excel' },
              { activity: 'Export Practice', duration: '30 min', description: 'Export query results to Excel' },
              { activity: 'Excel Analysis', duration: '15 min', description: 'Create charts from exported data' }
            ],
            materials: ['Student database'],
            assessment: 'Successfully export and use data',
            homework: 'Review spreadsheet functions'
          },
          {
            day: 'Tuesday',
            title: 'Data Integrity in Databases',
            duration: '60 min',
            objectives: ['Apply data integrity rules', 'Prevent data errors'],
            introduction: 'Protecting data quality is essential in databases!',
            mainActivities: [
              { activity: 'Integrity Rules', duration: '20 min', description: 'Required fields, validation' },
              { activity: 'Practice', duration: '30 min', description: 'Add integrity rules to database' },
              { activity: 'Testing', duration: '10 min', description: 'Test rules work correctly' }
            ],
            materials: ['Student database'],
            assessment: 'Database with proper integrity rules',
            homework: 'Design integrity rules for hospital database'
          },
          {
            day: 'Wednesday',
            title: 'Spreadsheet Review',
            duration: '60 min',
            objectives: ['Review all spreadsheet concepts', 'Practice spreadsheet skills'],
            introduction: 'Let\'s make sure we remember all our spreadsheet skills!',
            mainActivities: [
              { activity: 'Concept Review', duration: '20 min', description: 'Review formulas, functions, charts' },
              { activity: 'Practice Task', duration: '30 min', description: 'Complete spreadsheet challenge' },
              { activity: 'Q&A', duration: '10 min', description: 'Answer student questions' }
            ],
            materials: ['Review materials'],
            assessment: 'Complete review task',
            homework: 'Study weak areas'
          },
          {
            day: 'Thursday',
            title: 'Database Review',
            duration: '60 min',
            objectives: ['Review all database concepts', 'Practice database skills'],
            introduction: 'Review time for databases - tables, queries, reports!',
            mainActivities: [
              { activity: 'Concept Review', duration: '20 min', description: 'Review tables, queries, reports' },
              { activity: 'Practice Task', duration: '30 min', description: 'Complete database challenge' },
              { activity: 'Q&A', duration: '10 min', description: 'Answer student questions' }
            ],
            materials: ['Review materials'],
            assessment: 'Complete review task',
            homework: 'Final preparation for exam'
          },
          {
            day: 'Friday',
            title: 'Term 2 Exam',
            duration: '60 min',
            objectives: ['Complete Term 2 examination', 'Demonstrate all skills'],
            introduction: 'Show everything you\'ve learned in Term 2!',
            mainActivities: [
              { activity: 'Theory Exam', duration: '30 min', description: 'Written test on concepts' },
              { activity: 'Practical Exam', duration: '30 min', description: 'Spreadsheet and database tasks' }
            ],
            materials: ['Exam papers'],
            assessment: 'Term 2 Final Examination',
            homework: 'Enjoy term break!'
          }
        ]
      },
      {
        week: 10,
        unit: 'Unit 6: Databases I',
        topic: 'Catch-up and Extension Activities',
        strand: 'Strand 2: Application Software',
        subStrand: '2.3 - Databases I',
        learningOutcomes: [
          'Complete any outstanding work',
          'Extend learning for advanced students',
          'Prepare for Term 3'
        ],
        activities: ['Catch-up work', 'Extension projects', 'Term preparation'],
        resources: ['Computer lab', 'Extension materials'],
        dailyLessons: [
          {
            day: 'Monday',
            title: 'Catch-up Day 1',
            duration: '60 min',
            objectives: ['Complete incomplete work', 'Get individual help'],
            introduction: 'Use this time to finish any work you haven\'t completed.',
            mainActivities: [
              { activity: 'Individual Work', duration: '50 min', description: 'Students work on incomplete tasks' },
              { activity: 'Teacher Support', duration: '10 min', description: 'One-on-one help as needed' }
            ],
            materials: ['Student work folders'],
            assessment: 'Progress on incomplete work',
            homework: 'Continue working on incomplete tasks'
          },
          {
            day: 'Tuesday',
            title: 'Extension - Advanced Queries',
            duration: '60 min',
            objectives: ['Learn advanced query techniques', 'Challenge advanced students'],
            introduction: 'For those who have completed all work - let\'s go further!',
            mainActivities: [
              { activity: 'Parameter Queries', duration: '30 min', description: 'Create queries that ask for input' },
              { activity: 'Action Queries', duration: '30 min', description: 'Update and delete queries' }
            ],
            materials: ['Advanced query guide'],
            assessment: 'Create advanced queries',
            homework: 'Research database careers'
          },
          {
            day: 'Wednesday',
            title: 'Extension - Database Design',
            duration: '60 min',
            objectives: ['Design multi-table databases', 'Understand relationships'],
            introduction: 'Real databases have many related tables!',
            mainActivities: [
              { activity: 'Relationships Demo', duration: '25 min', description: 'Show table relationships' },
              { activity: 'Design Challenge', duration: '35 min', description: 'Design school management database' }
            ],
            materials: ['Design templates'],
            assessment: 'Database design document',
            homework: 'Complete design on paper'
          },
          {
            day: 'Thursday',
            title: 'Term 3 Preview',
            duration: '60 min',
            objectives: ['Preview programming concepts', 'Build excitement for next term'],
            introduction: 'Next term we learn programming - creating our own software!',
            mainActivities: [
              { activity: 'Programming Intro', duration: '25 min', description: 'What is programming?' },
              { activity: 'Simple Demo', duration: '25 min', description: 'Show simple program examples' },
              { activity: 'Discussion', duration: '10 min', description: 'What do you want to create?' }
            ],
            materials: ['Programming examples'],
            assessment: 'Interest and understanding shown',
            homework: 'Think about a program you\'d like to create'
          },
          {
            day: 'Friday',
            title: 'Term 2 Celebration and Wrap-up',
            duration: '60 min',
            objectives: ['Celebrate achievements', 'Prepare for term break'],
            introduction: 'Great work this term! Let\'s celebrate your achievements!',
            mainActivities: [
              { activity: 'Achievement Review', duration: '20 min', description: 'Review what we accomplished' },
              { activity: 'Certificate Presentation', duration: '20 min', description: 'Recognize top performers' },
              { activity: 'Clean-up', duration: '20 min', description: 'Organize files, clean workstations' }
            ],
            materials: ['Certificates'],
            assessment: 'Participation and cleanup',
            homework: 'Have a great term break!'
          }
        ]
      }
    ]
  },
  term3: {
    name: 'Term 3',
    focus: 'Programming & Graphics',
    weeks: 9,
    scheme: [
      // UNIT 7: Programming I (Weeks 18-21) - Strand 1, Sub-strand 1.3
      {
        week: 1,
        unit: 'Unit 7: Programming I',
        topic: 'Problem Solving and Program Planning',
        strand: 'Strand 1: Personal Computer Systems',
        subStrand: '1.3 - Programming I',
        learningOutcomes: [
          'Identify a problem that requires a program solution',
          'Decompose the problem into major components',
          'Define problem solving in programming',
          'List the main stages of programming'
        ],
        activities: ['Problem identification', 'Problem decomposition', 'Planning stages'],
        resources: ['Programming guide', 'Problem examples']
      },
      {
        week: 2,
        unit: 'Unit 7: Programming I',
        topic: 'Pseudocode and Algorithms',
        strand: 'Strand 1: Personal Computer Systems',
        subStrand: '1.3 - Programming I',
        learningOutcomes: [
          'Define program planning, analysis, design, coding, and maintenance',
          'Outline solution using pseudocode and algorithm',
          'Present solution using flow diagram components'
        ],
        activities: ['Pseudocode writing', 'Algorithm development', 'Flowcharting'],
        resources: ['Flowchart templates', 'Pseudocode examples']
      },
      {
        week: 3,
        unit: 'Unit 7: Programming I',
        topic: 'Variables and Data Structures',
        strand: 'Strand 1: Personal Computer Systems',
        subStrand: '1.3 - Programming I',
        learningOutcomes: [
          'Define computer programming language and syntax',
          'Write programming code with correct syntax',
          'Define and declare correct variables in a program',
          'Define and assign appropriate data structures'
        ],
        activities: ['Variable declaration', 'Data type practice', 'Code syntax'],
        resources: ['Programming environment', 'Syntax reference']
      },
      {
        week: 4,
        unit: 'Unit 7: Programming I',
        topic: 'Control Structures and Debugging',
        strand: 'Strand 1: Personal Computer Systems',
        subStrand: '1.3 - Programming I',
        learningOutcomes: [
          'Define and apply control structures in programming',
          'Write programming code using proper program editor',
          'Produce a program which fulfils requirements',
          'Define program bugs and types of programming errors',
          'Define syntax error and logic error'
        ],
        activities: ['Control structure practice', 'Program writing', 'Debugging exercises'],
        resources: ['Program editor', 'Debug examples']
      },
      // UNIT 8: Keyboard Skills (Week 22) - Strand 1, Sub-strand 1.4
      {
        week: 5,
        unit: 'Unit 8: Keyboard Skills',
        topic: 'Touch Typing and Speed',
        strand: 'Strand 1: Personal Computer Systems',
        subStrand: '1.4 - Keyboard Skills',
        learningOutcomes: [
          'Place fingers correctly on the keyboard',
          'Pick position of all letters correctly on the keyboard',
          'Use correct fingers on appropriate keys',
          'Use shift key correctly for capital letters',
          'Turn Caps Lock ON and OFF correctly',
          'Type a passage at a specified rate',
          'Type without looking at the keyboard',
          'Use correct fingers for typing numbers and decimal point'
        ],
        activities: ['Typing practice', 'Speed tests', 'Accuracy exercises'],
        resources: ['Typing software', 'Practice passages']
      },
      // UNIT 9: Desktop Publishing (Weeks 23-24) - Strand 3, Sub-strand 3.1
      {
        week: 6,
        unit: 'Unit 9: Desktop Publishing',
        topic: 'DTP Concepts and Documents',
        strand: 'Strand 3: Graphics Manipulation',
        subStrand: '3.1 - Desktop Publishing on a PC',
        learningOutcomes: [
          'Define Desktop Publishing',
          'Identify and outline key requirements for a DTP application',
          'Produce basic and full sketches for DTP documents',
          'Identify DTP applications',
          'State and outline main elements of DTP'
        ],
        activities: ['DTP introduction', 'Document sketching', 'Element identification'],
        resources: ['DTP software', 'Design templates']
      },
      {
        week: 7,
        unit: 'Unit 9: Desktop Publishing',
        topic: 'Creating DTP Documents',
        strand: 'Strand 3: Graphics Manipulation',
        subStrand: '3.1 - Desktop Publishing on a PC',
        learningOutcomes: [
          'Open existing and create new DTP files',
          'Produce DTP documents with required elements',
          'Manipulate elements of DTP documents',
          'Print DTP documents'
        ],
        activities: ['DTP creation', 'Element manipulation', 'Printing practice'],
        resources: ['DTP software', 'Printer access']
      },
      // UNIT 10: Computer Graphics I (Weeks 25-26) - Strand 3, Sub-strand 3.2
      {
        week: 8,
        unit: 'Unit 10: Computer Graphics I',
        topic: 'Graphic Design Basics',
        strand: 'Strand 3: Graphics Manipulation',
        subStrand: '3.2 - Computer Graphics I',
        learningOutcomes: [
          'Identify and outline requirements for computer graphics',
          'Identify computer graphic design applications',
          'State and outline basic elements of graphic design',
          'Produce basic and full sketches for graphic design'
        ],
        activities: ['Graphics introduction', 'Design elements', 'Sketching'],
        resources: ['Graphics software', 'Design examples']
      },
      {
        week: 9,
        unit: 'Unit 10: Computer Graphics I',
        topic: 'Creating Graphics and Term 3 Assessment',
        strand: 'Strand 3: Graphics Manipulation',
        subStrand: '3.2 - Computer Graphics I',
        learningOutcomes: [
          'Produce computer graphic designs using design tools',
          'Apply appropriate computer graphic tools on projects',
          'Apply elements of design on graphic design projects',
          'Print computer graphics'
        ],
        activities: ['Graphics creation', 'Design project', 'Term 3 assessment'],
        resources: ['Graphics software', 'Assessment materials'],
        assessment: 'CA Task 3 - Teacher Designed Task'
      }
    ]
  },
  term4: {
    name: 'Term 4',
    focus: 'Graphics, Presentations & Networking',
    weeks: 7,
    scheme: [
      // UNIT 11: Digital Image Processing (Week 27) - Strand 3, Sub-strand 3.3
      {
        week: 1,
        unit: 'Unit 11: Digital Image Processing',
        topic: 'Image Manipulation',
        strand: 'Strand 3: Graphics Manipulation',
        subStrand: '3.3 - Digital Image Processing I',
        learningOutcomes: [
          'Define digital image processing',
          'Identify digital image processing applications',
          'State and outline key features of digital imaging applications',
          'Open digital image processing objects',
          'Apply image modifications using imaging software',
          'Describe procedures used in image modification',
          'Apply advance imaging features (collage, panorama)',
          'Export processed images to other applications',
          'Apply image dimensions and orientations',
          'Print processed images'
        ],
        activities: ['Image editing', 'Collage creation', 'Image export'],
        resources: ['Image editing software', 'Sample images']
      },
      // UNIT 12: Presentation Software (Weeks 28-29) - Strand 3, Sub-strand 3.4
      {
        week: 2,
        unit: 'Unit 12: Presentation Software',
        topic: 'Planning and Creating Presentations',
        strand: 'Strand 3: Graphics Manipulation',
        subStrand: '3.4 - Presentation Software',
        learningOutcomes: [
          'Produce components of a presentation plan',
          'Design presentations with key elements',
          'Identify presentation applications',
          'Open existing and create new presentation files',
          'Create basic text-based and multi-slide presentations'
        ],
        activities: ['Presentation planning', 'Slide creation', 'Content design'],
        resources: ['Presentation software', 'Planning templates']
      },
      {
        week: 3,
        unit: 'Unit 12: Presentation Software',
        topic: 'Advanced Features and Presenting',
        strand: 'Strand 3: Graphics Manipulation',
        subStrand: '3.4 - Presentation Software',
        learningOutcomes: [
          'Produce full presentations using different elements',
          'Apply specific and advanced features (animation)',
          'Present complete presentations to an audience'
        ],
        activities: ['Animation practice', 'Full presentation', 'Presenting skills'],
        resources: ['Presentation software', 'Projector']
      },
      // UNIT 13: Networks I (Week 30) - Strand 4, Sub-strand 4.1
      {
        week: 4,
        unit: 'Unit 13: Networks I',
        topic: 'Networking Concepts',
        strand: 'Strand 4: Communication and Network Systems',
        subStrand: '4.1 - Networks I',
        learningOutcomes: [
          'Define data communication protocols',
          'Outline different types of data communication protocols',
          'State and describe the purpose of protocols',
          'Outline key features of TCP/IP protocol',
          'State and outline advantages of data networks',
          'Outline different types of data networks',
          'Explain concepts behind types of data networks',
          'Define and describe data transmission',
          'Name common data networking devices and their functions',
          'Define data network topology',
          'Outline different types of data networking topologies',
          'Present sketches of networking topologies and diagrams'
        ],
        activities: ['Protocol introduction', 'Network types', 'TCP/IP basics'],
        resources: ['Network diagrams', 'Protocol charts']
      },
      // UNIT 14: Web & Internet (Weeks 31-32) - Strand 4, Sub-strands 4.2, 4.3
      {
        week: 5,
        unit: 'Unit 14: Web & Internet',
        topic: 'HTML and Web Browsers',
        strand: 'Strand 4: Communication and Network Systems',
        subStrand: '4.2 - Web Page I, 4.3 - The Internet I',
        learningOutcomes: [
          'Identify appropriate topic for a website',
          'State main purpose for a website',
          'Outline requirements for a website',
          'Identify target audience for a website',
          'Identify and manage content for the website',
          'Create and manage folders for website files',
          'Define HTML',
          'Use text editor to write basic HTML code',
          'Produce webpages with hyperlinks to contents',
          'Define World Wide Web (www)',
          'Define web browser and URL',
          'Identify and outline key web browsers',
          'Name known search engines'
        ],
        activities: ['HTML coding', 'Webpage creation', 'Browser navigation'],
        resources: ['Text editor', 'Web browser']
      },
      {
        week: 6,
        unit: 'Unit 14: Web & Internet',
        topic: 'Search and Web Research',
        strand: 'Strand 4: Communication and Network Systems',
        subStrand: '4.3 - The Internet I',
        learningOutcomes: [
          'Use web browser to search for websites using URL',
          'Use search engine to find specific information',
          'Search using single and multiple keywords',
          'Outline key components of the World Wide Web',
          'Explain structure of the World Wide Web',
          'Save and use contents from the World Wide Web',
          'Print information from the World Wide Web',
          'Use web browser to test web pages'
        ],
        activities: ['Web searching', 'Research practice', 'Content saving'],
        resources: ['Internet access', 'Search engines']
      },
      // UNIT 15: Email Communication (Week 33) - Strand 4, Sub-strand 4.4
      {
        week: 7,
        unit: 'Unit 15: Email Communication',
        topic: 'Email and Term 4 Assessment',
        strand: 'Strand 4: Communication and Network Systems',
        subStrand: '4.4 - Exchange Messages Using Email',
        learningOutcomes: [
          'Identify public email providers and email applications',
          'State difference between email providers and applications',
          'Create public email accounts',
          'Outline key components of email addresses and messages',
          'State function of each email message component',
          'Prepare and write full email messages',
          'Write emails with attachments',
          'Send email messages successfully',
          'Define Email SPAM, computer viruses, and Malware',
          'Outline concerns with SPAM, viruses, and Malware',
          'Explain security measures to protect email users',
          'Outline data integrity measures when using email'
        ],
        activities: ['Email setup', 'Email composition', 'Term 4 assessment'],
        resources: ['Email access', 'Assessment materials'],
        assessment: 'CA Task 4 - Common Assessment Task + Internal Examination'
      }
    ]
  }
};

// ==========================================
// YEAR 12 ICT WEEKLY LESSONS
// Aligned with year12ICTYearlyPlanner
// ==========================================
export const year12ICTWeeklyLessons = {
  term1: {
    name: 'Term 1',
    focus: 'PC Systems & Hardware Maintenance',
    weeks: 10,
    scheme: [
      // Unit 1: PC Hardware Components (Weeks 1-3)
      {
        week: 1,
        unit: 'Unit 1: PC Hardware Components',
        topic: 'Introduction to PC Hardware Identification',
        strand: 'Strand 1: PC Systems',
        subStrand: '12ICT1.1 - Computer Hardware Maintenance',
        learningOutcomes: [
          'Identify major hardware components of a PC',
          'Test that the PC can be powered on',
          'Name/Identify an Operating System (OS) for the PC'
        ],
        activities: ['Hardware identification practical', 'PC power testing', 'OS recognition'],
        resources: ['PC components', 'Lab computers']
      },
      {
        week: 2,
        unit: 'Unit 1: PC Hardware Components',
        topic: 'PC Ports and Cables',
        strand: 'Strand 1: PC Systems',
        subStrand: '12ICT1.1 - Computer Hardware Maintenance',
        learningOutcomes: [
          'Identify common PC ports',
          'Identify common PC cables',
          'Match cables to appropriate ports'
        ],
        activities: ['Port identification', 'Cable matching exercise', 'Connection practice'],
        resources: ['Various cables', 'Port diagrams']
      },
      {
        week: 3,
        unit: 'Unit 1: PC Hardware Components',
        topic: 'Hardware Documentation',
        strand: 'Strand 1: PC Systems',
        subStrand: '12ICT1.1 - Computer Hardware Maintenance',
        learningOutcomes: [
          'Assess and report hardware issues after running a diagnostic test',
          'Document hardware configurations',
          'Create hardware inventory'
        ],
        activities: ['Hardware diagnostics', 'Inventory creation', 'Documentation practice'],
        resources: ['Diagnostic tools', 'Inventory templates']
      },
      // Unit 2: Building a PC (Weeks 4-6)
      {
        week: 4,
        unit: 'Unit 2: Building a PC',
        topic: 'PC Assembly Safety and Procedures',
        strand: 'Strand 1: PC Systems',
        subStrand: '12ICT1.1 - Computer Hardware Maintenance',
        learningOutcomes: [
          'Build a PC with major hardware components',
          'Follow safety procedures for PC assembly',
          'Identify PC hardware diagnostic tools'
        ],
        activities: ['Safety procedures', 'Component installation', 'Diagnostic tools overview'],
        resources: ['PC components', 'Anti-static equipment']
      },
      {
        week: 5,
        unit: 'Unit 2: Building a PC',
        topic: 'Component Installation',
        strand: 'Strand 1: PC Systems',
        subStrand: '12ICT1.1 - Computer Hardware Maintenance',
        learningOutcomes: [
          'Install CPU, RAM, storage devices',
          'Connect power supply cables',
          'Perform basic hardware maintenance tasks'
        ],
        activities: ['CPU installation', 'RAM installation', 'Power connection'],
        resources: ['PC components', 'Installation guides']
      },
      {
        week: 6,
        unit: 'Unit 2: Building a PC',
        topic: 'Build Testing and Troubleshooting',
        strand: 'Strand 1: PC Systems',
        subStrand: '12ICT1.1 - Computer Hardware Maintenance',
        learningOutcomes: [
          'Build a working PC with all required hardware components',
          'Recommend proper fixes for hardware issues',
          'Apply possible solutions to a PC problem after performing proper diagnostic approaches'
        ],
        activities: ['First boot testing', 'Problem diagnosis', 'Troubleshooting practice'],
        resources: ['Completed PC builds', 'Diagnostic software']
      },
      // Unit 3: Programming II (Weeks 7-8) - Sub-strand 12ICT1.3
      {
        week: 7,
        unit: 'Unit 3: Programming II',
        topic: 'Structured Programming & Software Development',
        strand: 'Strand 1: PC Systems',
        subStrand: '12ICT1.3 - Programming II',
        learningOutcomes: [
          'Define structured programming and its major components',
          'State key features of software development methodologies',
          'Present a program design using a basic software development methodology',
          'Name/Identify a programming language and program editor'
        ],
        activities: ['Structured programming concepts', 'SDLC phases overview', 'Program design basics'],
        resources: ['Programming guides', 'IDE software']
      },
      {
        week: 8,
        unit: 'Unit 3: Programming II',
        topic: 'Coding and Program Testing',
        strand: 'Strand 1: PC Systems',
        subStrand: '12ICT1.3 - Programming II',
        learningOutcomes: [
          'Declare variables and arrays using correct syntax',
          'Apply conditional statements and control structures',
          'Create program functions that perform specific tasks',
          'Run the program and perform proper debugging'
        ],
        activities: ['Variable declaration', 'Control structure coding', 'Function creation', 'Debugging practice'],
        resources: ['Code editor', 'Programming worksheets']
      },
      // Unit 4: Keyboard Skills II (Week 9) - Sub-strand 12ICT1.4
      {
        week: 9,
        unit: 'Unit 4: Keyboard Skills II',
        topic: 'Touch Typing and Speed Development',
        strand: 'Strand 1: PC Systems',
        subStrand: '12ICT1.4 - Keyboard Skills II',
        learningOutcomes: [
          'Place fingers correctly on home keys',
          'Use correct placement of all fingers on appropriate keys',
          'Type a passage of information at a rate of 30 words per minute',
          'Use correct fingers for inputting numbers 0 to 9'
        ],
        activities: ['Home key positioning', 'Touch typing practice', 'Speed tests', 'Number pad exercises'],
        resources: ['Typing software', 'Speed test tools']
      },
      // Unit 5: Term 1 Review & Assessment (Week 10)
      {
        week: 10,
        unit: 'Unit 5: Term 1 Review & Assessment',
        topic: 'Term 1 Assessment',
        strand: 'Strand 1: PC Systems',
        subStrand: '12ICT1.1, 12ICT1.3, 12ICT1.4',
        learningOutcomes: [
          'Review all Term 1 hardware, programming, and keyboard concepts',
          'Demonstrate practical PC building and programming skills',
          'Complete Term 1 assessment'
        ],
        activities: ['Revision exercises', 'Practical exam preparation', 'Theory and practical exam'],
        resources: ['Review materials', 'Assessment papers'],
        assessment: 'Term 1 Theory & Practical Exam'
      }
    ]
  },
  term2: {
    name: 'Term 2',
    focus: 'Application Software (Word Processing & Spreadsheets)',
    weeks: 10,
    scheme: [
      // Unit 5: Advanced Word Processing (Weeks 11-13)
      {
        week: 1,
        unit: 'Unit 5: Advanced Word Processing',
        topic: 'Advanced Formatting Features',
        strand: 'Strand 2: Application Software',
        subStrand: '12ICT2.1 - Word Processing II',
        learningOutcomes: [
          'Outline the main differences between text processors and word processors',
          'Format characters using the ribbon',
          'Apply required editing features on a document'
        ],
        activities: ['Advanced formatting practice', 'Style application', 'Document structure'],
        resources: ['Word processing software', 'Formatting guides']
      },
      {
        week: 2,
        unit: 'Unit 5: Advanced Word Processing',
        topic: 'Document Elements and Features',
        strand: 'Strand 2: Application Software',
        subStrand: '12ICT2.1 - Word Processing II',
        learningOutcomes: [
          'Produce a document with multiple inserted features',
          'Insert headers, footers, page numbers, images, tables',
          'Apply a footnote'
        ],
        activities: ['Element insertion', 'Header/footer setup', 'Table creation'],
        resources: ['Word processing software', 'Sample documents']
      },
      {
        week: 3,
        unit: 'Unit 5: Advanced Word Processing',
        topic: 'Professional Documents',
        strand: 'Strand 2: Application Software',
        subStrand: '12ICT2.1 - Word Processing II',
        learningOutcomes: [
          'Create a document using specific formatting features',
          'Produce a full document using all required formatting features',
          'Import graphs and tables from spreadsheets'
        ],
        activities: ['Professional document creation', 'Data import', 'Document refinement'],
        resources: ['Word processing software', 'Project requirements']
      },
      // Unit 6: Mail Merge & Citations (Weeks 14-15)
      {
        week: 4,
        unit: 'Unit 6: Mail Merge & Citations',
        topic: 'Mail Merge Fundamentals',
        strand: 'Strand 2: Application Software',
        subStrand: '12ICT2.1 - Word Processing II',
        learningOutcomes: [
          'Use major components of the mail merge feature',
          'Create data sources for mail merge',
          'Produce a customised document using the mail merge feature'
        ],
        activities: ['Mail merge setup', 'Data source creation', 'Personalized letters'],
        resources: ['Word processing software', 'Mail merge templates']
      },
      {
        week: 5,
        unit: 'Unit 6: Mail Merge & Citations',
        topic: 'Citations and Bibliography',
        strand: 'Strand 2: Application Software',
        subStrand: '12ICT2.1 - Word Processing II',
        learningOutcomes: [
          'Create a specific citation',
          'Insert specific citations for a bibliography',
          'Produce a complete customised document using mail merge'
        ],
        activities: ['Citation creation', 'Bibliography generation', 'Academic referencing'],
        resources: ['Word processing software', 'Citation guides']
      },
      // Unit 7: Spreadsheet Fundamentals (Weeks 16-18)
      {
        week: 6,
        unit: 'Unit 7: Spreadsheet Fundamentals',
        topic: 'Data Entry and Cell Referencing',
        strand: 'Strand 2: Application Software',
        subStrand: '12ICT2.2 - Spreadsheets II',
        learningOutcomes: [
          'Enter all required values into a worksheet',
          'Apply editing features on a worksheet',
          'Apply a cell reference (relative or absolute)'
        ],
        activities: ['Data entry', 'Formatting practice', 'Cell referencing'],
        resources: ['Spreadsheet software', 'Practice data']
      },
      {
        week: 7,
        unit: 'Unit 7: Spreadsheet Fundamentals',
        topic: 'Formulas and Functions',
        strand: 'Strand 2: Application Software',
        subStrand: '12ICT2.2 - Spreadsheets II',
        learningOutcomes: [
          'Produce a worksheet with a complete formula',
          'Apply mixed cell references',
          'Test all specified formulae in a spreadsheet'
        ],
        activities: ['Formula creation', 'Function application', 'Formula testing'],
        resources: ['Spreadsheet software', 'Formula reference']
      },
      {
        week: 8,
        unit: 'Unit 7: Spreadsheet Fundamentals',
        topic: 'Data Manipulation',
        strand: 'Strand 2: Application Software',
        subStrand: '12ICT2.2 - Spreadsheets II',
        learningOutcomes: [
          'Manipulate given data on multiple worksheets',
          'Produce a formatted worksheet containing values with required formula',
          'Apply data integrity practices'
        ],
        activities: ['Multi-worksheet work', 'Data formatting', 'Integrity checks'],
        resources: ['Spreadsheet software', 'Practice datasets']
      },
      // Unit 8: Charts & Data Analysis (Weeks 19-20)
      {
        week: 9,
        unit: 'Unit 8: Charts & Data Analysis',
        topic: 'Creating Charts and Graphs',
        strand: 'Strand 2: Application Software',
        subStrand: '12ICT2.2 - Spreadsheets II',
        learningOutcomes: [
          'Produce a simple spreadsheet graph',
          'Produce a spreadsheet graph to represent a set of data',
          'Export spreadsheet items to other applications'
        ],
        activities: ['Chart creation', 'Graph formatting', 'Data export'],
        resources: ['Spreadsheet software', 'Data sets']
      },
      {
        week: 10,
        unit: 'Unit 8: Charts & Data Analysis',
        topic: 'Term 2 Assessment',
        strand: 'Strand 2: Application Software',
        subStrand: '12ICT2.2 - Spreadsheets II',
        learningOutcomes: [
          'Produce graphs to visually display results from analysing data',
          'Apply data integrity practices on a given spreadsheet project',
          'Complete Term 2 assessment'
        ],
        activities: ['Data analysis', 'Graph interpretation', 'Term 2 exam'],
        resources: ['Assessment materials'],
        assessment: 'Term 2 Theory & Practical Exam'
      }
    ]
  },
  term3: {
    name: 'Term 3',
    focus: 'Databases, Graphics & Programming',
    weeks: 10,
    scheme: [
      // Unit 9: Database Fundamentals (Weeks 21-23)
      {
        week: 1,
        unit: 'Unit 9: Database Fundamentals',
        topic: 'Database Structures and Concepts',
        strand: 'Strand 2: Application Software',
        subStrand: '12ICT2.3 - Databases II',
        learningOutcomes: [
          'Outline three different types of database structures',
          'Compare known types of database structures',
          'Describe the concept of relational databases'
        ],
        activities: ['Database structure comparison', 'Relational concepts', 'Database design'],
        resources: ['Database software', 'Structure diagrams']
      },
      {
        week: 2,
        unit: 'Unit 9: Database Fundamentals',
        topic: 'Table Creation and Relationships',
        strand: 'Strand 2: Application Software',
        subStrand: '12ICT2.3 - Databases II',
        learningOutcomes: [
          'Create a complete relational database table using the table wizard',
          'Apply appropriate field sizes and types',
          'Outline the known types of relationships within a relational database'
        ],
        activities: ['Table creation', 'Field configuration', 'Relationship setup'],
        resources: ['Database software', 'Database design templates']
      },
      {
        week: 3,
        unit: 'Unit 9: Database Fundamentals',
        topic: 'Multi-Table Databases',
        strand: 'Strand 2: Application Software',
        subStrand: '12ICT2.3 - Databases II',
        learningOutcomes: [
          'Create a relational database with multiple tables linked together',
          'Apply editing features correctly on database tables',
          'Explain the key advantages of relational databases'
        ],
        activities: ['Multi-table database', 'Relationship linking', 'Data editing'],
        resources: ['Database software', 'Practice scenarios']
      },
      // Unit 10: Forms, Queries & Reports (Weeks 24-25)
      {
        week: 4,
        unit: 'Unit 10: Forms, Queries & Reports',
        topic: 'Forms and Data Entry',
        strand: 'Strand 2: Application Software',
        subStrand: '12ICT2.3 - Databases II',
        learningOutcomes: [
          'Create multiple forms using the form wizard',
          'Apply formatting tasks to existing forms',
          'Enter data using forms'
        ],
        activities: ['Form creation', 'Form formatting', 'Data entry via forms'],
        resources: ['Database software', 'Form design templates']
      },
      {
        week: 5,
        unit: 'Unit 10: Forms, Queries & Reports',
        topic: 'SQL Queries and Reports',
        strand: 'Strand 2: Application Software',
        subStrand: '12ICT2.3 - Databases II',
        learningOutcomes: [
          'Create key components of a query using SQL',
          'Create a complete query with specific conditions',
          'Create multiple reports from a database'
        ],
        activities: ['SQL query writing', 'Query execution', 'Report generation'],
        resources: ['Database software', 'SQL reference']
      },
      // Unit 11: Digital Image Processing (Weeks 26-27)
      {
        week: 6,
        unit: 'Unit 11: Digital Image Processing',
        topic: 'Image File Formats and Editing',
        strand: 'Strand 3: Graphics',
        subStrand: '12ICT3.3 - Digital Image Processing II',
        learningOutcomes: [
          'List different file types compatible with imaging applications',
          'Modify image properties using imaging tools',
          'Save and export processed images'
        ],
        activities: ['Image format exploration', 'Basic editing', 'File management'],
        resources: ['Image editing software', 'Sample images']
      },
      {
        week: 7,
        unit: 'Unit 11: Digital Image Processing',
        topic: 'Advanced Image Processing',
        strand: 'Strand 3: Graphics',
        subStrand: '12ICT3.3 - Digital Image Processing II',
        learningOutcomes: [
          'Produce a complete collage image using appropriate collage maker',
          'Produce a complete panorama image using appropriate tool',
          'Modify image properties according to required specifics'
        ],
        activities: ['Collage creation', 'Panorama assembly', 'Advanced editing'],
        resources: ['Image editing software', 'Photo collection']
      },
      // Unit 12: Programming Fundamentals (Weeks 28-30)
      {
        week: 8,
        unit: 'Unit 12: Programming Fundamentals',
        topic: 'Structured Programming Concepts',
        strand: 'Strand 1: PC Systems',
        subStrand: '12ICT1.3 - Programming II',
        learningOutcomes: [
          'Outline major components of structured programming',
          'Define sequence, repetition, selection in structured programming',
          'List some of the known software development methodologies'
        ],
        activities: ['Programming concepts', 'Structure practice', 'Methodology overview'],
        resources: ['Programming environment', 'Concept diagrams']
      },
      {
        week: 9,
        unit: 'Unit 12: Programming Fundamentals',
        topic: 'Variables, Arrays, and Control Structures',
        strand: 'Strand 1: PC Systems',
        subStrand: '12ICT1.3 - Programming II',
        learningOutcomes: [
          'Declare variables correctly as required coding elements',
          'Declare an array with required elements',
          'Apply conditional statements to meet coding requirements'
        ],
        activities: ['Variable practice', 'Array declaration', 'Conditional coding'],
        resources: ['Programming environment', 'Code examples']
      },
      {
        week: 10,
        unit: 'Unit 12: Programming Fundamentals',
        topic: 'Program Creation and Term 3 Assessment',
        strand: 'Strand 1: PC Systems',
        subStrand: '12ICT1.3 - Programming II',
        learningOutcomes: [
          'Produce part of a program code using structured programming',
          'Run the program to present required output',
          'Perform proper program debugging'
        ],
        activities: ['Program creation', 'Testing and debugging', 'Term 3 exam'],
        resources: ['Programming environment', 'Assessment materials'],
        assessment: 'Term 3 Theory & Practical Exam'
      }
    ]
  },
  term4: {
    name: 'Term 4',
    focus: 'Networking, Web Development & Security',
    weeks: 10,
    scheme: [
      // Unit 13: Computer Networks (Weeks 31-33)
      {
        week: 1,
        unit: 'Unit 13: Computer Networks',
        topic: 'Network Concepts and Protocols',
        strand: 'Strand 4: Communication & Networks',
        subStrand: '12ICT4.1 - Networks II',
        learningOutcomes: [
          'Outline key importance of having a data communication network',
          'Outline basic concepts of data networking',
          'Outline/List the major components of TCP/IP'
        ],
        activities: ['Network concepts', 'Protocol overview', 'TCP/IP exploration'],
        resources: ['Network diagrams', 'Protocol charts']
      },
      {
        week: 2,
        unit: 'Unit 13: Computer Networks',
        topic: 'DNS and Network Configuration',
        strand: 'Strand 4: Communication & Networks',
        subStrand: '12ICT4.1 - Networks II',
        learningOutcomes: [
          'Describe the main functions of TCP/IP',
          'Outline/List the functions of DNS',
          'Configure all required networking settings of connected PCs'
        ],
        activities: ['DNS configuration', 'IP addressing', 'Network settings'],
        resources: ['Network equipment', 'Configuration guides']
      },
      {
        week: 3,
        unit: 'Unit 13: Computer Networks',
        topic: 'Network Hardware and Setup',
        strand: 'Strand 4: Communication & Networks',
        subStrand: '12ICT4.1 - Networks II',
        learningOutcomes: [
          'Make working RJ-45 connectors using a crimping tool',
          'Install network cards on PCs',
          'Present a basic connected data network'
        ],
        activities: ['Cable making', 'NIC installation', 'Network setup'],
        resources: ['Crimping tools', 'Network cables', 'NICs']
      },
      // Unit 14: Web Page Creation (Weeks 34-36)
      {
        week: 4,
        unit: 'Unit 14: Web Page Creation',
        topic: 'Web Hosting and CMS Concepts',
        strand: 'Strand 4: Communication & Networks',
        subStrand: '12ICT4.2 - Web Page II',
        learningOutcomes: [
          'Identify a free web hosting site with cpanel',
          'Outline key functions of web hosting sites',
          'Define content management system (CMS)'
        ],
        activities: ['Web hosting exploration', 'CMS introduction', 'Account setup'],
        resources: ['Internet access', 'Web hosting platforms']
      },
      {
        week: 5,
        unit: 'Unit 14: Web Page Creation',
        topic: 'Website Planning and Design',
        strand: 'Strand 4: Communication & Networks',
        subStrand: '12ICT4.2 - Web Page II',
        learningOutcomes: [
          'Present the major components of a plan for a website layout',
          'Create required webpages using the content management tool',
          'Apply an appropriate template for the website'
        ],
        activities: ['Website planning', 'Page creation', 'Template application'],
        resources: ['CMS platform', 'Design templates']
      },
      {
        week: 6,
        unit: 'Unit 14: Web Page Creation',
        topic: 'Content Creation and Publishing',
        strand: 'Strand 4: Communication & Networks',
        subStrand: '12ICT4.2 - Web Page II',
        learningOutcomes: [
          'Enter required contents to complete the pages',
          'Apply an appropriate colour scheme',
          'Publish all required contents and test website'
        ],
        activities: ['Content entry', 'Design refinement', 'Website publishing'],
        resources: ['CMS platform', 'Content materials']
      },
      // Unit 15: Internet Security & Safety (Weeks 37-38)
      {
        week: 7,
        unit: 'Unit 15: Internet Security & Safety',
        topic: 'Cybercrimes and Threats',
        strand: 'Strand 4: Communication & Networks',
        subStrand: '12ICT4.3 - The Internet II: Security and Safety',
        learningOutcomes: [
          'Define cybercrimes',
          'List examples of cybercrimes',
          'Outline the concerns with cybercrimes'
        ],
        activities: ['Cybercrime overview', 'Threat identification', 'Impact discussion'],
        resources: ['Security materials', 'Case studies']
      },
      {
        week: 8,
        unit: 'Unit 15: Internet Security & Safety',
        topic: 'Security Measures and Data Protection',
        strand: 'Strand 4: Communication & Networks',
        subStrand: '12ICT4.3 - The Internet II: Security and Safety',
        learningOutcomes: [
          'List common cybersecurity measures',
          'Explain how cybersecurity measures protect computer systems',
          'Explain the importance of data security'
        ],
        activities: ['Security measures', 'Protection strategies', 'Data security'],
        resources: ['Security guidelines', 'Protection tools']
      },
      // Unit 16: Final Review & Examinations (Weeks 39-40)
      {
        week: 9,
        unit: 'Unit 16: Final Review',
        topic: 'Comprehensive Review',
        strand: 'All Strands',
        subStrand: 'All',
        learningOutcomes: [
          'Review all Year 12 ICT content',
          'Prepare for final examination',
          'Complete portfolio'
        ],
        activities: ['Revision sessions', 'Practice exams', 'Portfolio review'],
        resources: ['Review materials', 'Practice papers']
      },
      {
        week: 10,
        unit: 'Unit 16: Final Examinations',
        topic: 'Year 12 Final Examination',
        strand: 'All Strands',
        subStrand: 'All',
        learningOutcomes: [
          'Demonstrate mastery of Year 12 ICT content',
          'Complete practical examination',
          'Complete theory examination'
        ],
        activities: ['Final examination'],
        resources: ['Examination materials'],
        assessment: 'Final Year Examination'
      }
    ]
  }
};

// ==========================================
// YEAR 13 ICT WEEKLY LESSONS
// Aligned with year13ICTYearlyPlanner from year13ICTData.js
// ==========================================
export const year13ICTWeeklyLessons = {
  term1: {
    name: 'Term 1',
    focus: 'Personal Computer Systems & Digital Media',
    weeks: 10,
    scheme: [
      // Unit 1: Future ICT Trends (Weeks 1-4)
      {
        week: 1,
        unit: 'Unit 1: Future ICT Trends',
        topic: 'Future ICT Trends in Education',
        strand: 'Strand 1: Personal Computer Systems',
        subStrand: '13ICT1.1 - Future ICT Trends in Computer Systems',
        learningOutcomes: [
          'Identify a future ICT trend that can help in education',
          'State key features of future ICT trends in education',
          'Outline/List key features of identified future ICT trends'
        ],
        activities: ['E-learning research', 'Virtual classroom exploration', 'Trend analysis'],
        resources: ['Internet access', 'Research materials']
      },
      {
        week: 2,
        unit: 'Unit 1: Future ICT Trends',
        topic: 'Future ICT Trends in Health and Home',
        strand: 'Strand 1: Personal Computer Systems',
        subStrand: '13ICT1.1 - Future ICT Trends in Computer Systems',
        learningOutcomes: [
          'Identify future ICT trends that can help with health',
          'Identify future ICT trends that can help at home',
          'State key features of these trends'
        ],
        activities: ['Telemedicine research', 'Smart home exploration', 'IoT discussion'],
        resources: ['Internet access', 'Case studies']
      },
      {
        week: 3,
        unit: 'Unit 1: Future ICT Trends',
        topic: 'Impacts of Future ICT Trends',
        strand: 'Strand 1: Personal Computer Systems',
        subStrand: '13ICT1.1 - Future ICT Trends in Computer Systems',
        learningOutcomes: [
          'State positive and negative impacts of future ICT trends',
          'Outline the impacts on various sectors',
          'Analyse impacts of future ICT trends in key sectors of the economy'
        ],
        activities: ['Impact analysis', 'Sector research', 'Discussion and debate'],
        resources: ['Research materials', 'Analysis frameworks']
      },
      {
        week: 4,
        unit: 'Unit 1: Future ICT Trends',
        topic: 'Preparing Vanuatu for Future ICT',
        strand: 'Strand 1: Personal Computer Systems',
        subStrand: '13ICT1.1 - Future ICT Trends in Computer Systems',
        learningOutcomes: [
          'Identify future trends in the use of the Internet',
          'State important factors in preparing Vanuatu for future ICT',
          'Explain key factors for Vanuatu to consider'
        ],
        activities: ['Internet trends research', 'Vanuatu ICT planning', 'Project presentation'],
        resources: ['Internet access', 'Government ICT plans'],
        assessment: 'Research Project on Future ICT Trends (15%)'
      },
      // Unit 2: Management of Information Systems (Weeks 5-8)
      {
        week: 5,
        unit: 'Unit 2: Management of Information Systems',
        topic: 'Workflows and Information Systems',
        strand: 'Strand 1: Personal Computer Systems',
        subStrand: '13ICT1.2 - Management of Information Systems',
        learningOutcomes: [
          'Define workflow',
          'State examples of workflows used by local organisations',
          'Outline the main parts of a sample workflow'
        ],
        activities: ['Workflow definition', 'Local examples research', 'Workflow mapping'],
        resources: ['Workflow diagrams', 'Local business examples']
      },
      {
        week: 6,
        unit: 'Unit 2: Management of Information Systems',
        topic: 'IS Applications and Benefits',
        strand: 'Strand 1: Personal Computer Systems',
        subStrand: '13ICT1.2 - Management of Information Systems',
        learningOutcomes: [
          'Describe the relationship between a workflow and an IS',
          'Name IS applications used by local organisations',
          'State key benefits of IS in Education and Government'
        ],
        activities: ['IS application exploration', 'Benefit analysis', 'Case studies'],
        resources: ['IS examples', 'Case study materials']
      },
      {
        week: 7,
        unit: 'Unit 2: Management of Information Systems',
        topic: 'Information Systems Development',
        strand: 'Strand 1: Personal Computer Systems',
        subStrand: '13ICT1.2 - Management of Information Systems',
        learningOutcomes: [
          'Define Information Systems development',
          'State common methods for developing an Information System',
          'Outline the key steps in the IS development process'
        ],
        activities: ['IS development methods', 'SDLC overview', 'Development planning'],
        resources: ['Development methodology guides', 'SDLC diagrams']
      },
      {
        week: 8,
        unit: 'Unit 2: Management of Information Systems',
        topic: 'IS Development Process',
        strand: 'Strand 1: Personal Computer Systems',
        subStrand: '13ICT1.2 - Management of Information Systems',
        learningOutcomes: [
          'Explain the process of developing Information Systems',
          'Discuss the importance of workflows to an organisation',
          'Complete practical assessment on IS analysis'
        ],
        activities: ['Development process practice', 'IS analysis', 'Assessment'],
        resources: ['Assessment materials'],
        assessment: 'Practical Assessment - IS Analysis (10%)'
      },
      // Unit 3: Digital Media Outputs (Weeks 9-10)
      {
        week: 9,
        unit: 'Unit 3: Digital Media Outputs',
        topic: 'Digital Media Devices and Applications',
        strand: 'Strand 2: Application Software',
        subStrand: '13ICT2.1 - Digital Media Outputs',
        learningOutcomes: [
          'State the importance of digital media',
          'Identify required audio and video devices for specific events',
          'Outline functions of key digital media devices'
        ],
        activities: ['Media device identification', 'Device functions', 'Equipment setup'],
        resources: ['Audio/video equipment', 'Media software']
      },
      {
        week: 10,
        unit: 'Unit 3: Digital Media Outputs',
        topic: 'Creating Digital Media Content',
        strand: 'Strand 2: Application Software',
        subStrand: '13ICT2.1 - Digital Media Outputs',
        learningOutcomes: [
          'State key requirements for creating audio and video content',
          'Open and modify media files using digital media tools',
          'Create digital media portfolio'
        ],
        activities: ['Audio recording', 'Video creation', 'Portfolio development'],
        resources: ['Recording equipment', 'Editing software'],
        assessment: 'Digital Media Portfolio (10%)'
      }
    ]
  },
  term2: {
    name: 'Term 2',
    focus: 'ICT Infrastructure & Database-Driven Websites',
    weeks: 10,
    scheme: [
      // Unit 4: ICT Infrastructure (Weeks 1-4)
      {
        week: 1,
        unit: 'Unit 4: ICT Infrastructure',
        topic: 'Infrastructure Requirements',
        strand: 'Strand 1: Personal Computer Systems',
        subStrand: '13ICT1.3 - ICT Infrastructure',
        learningOutcomes: [
          'Identify ICT infrastructure requirements for an organisation',
          'Outline basic ICT infrastructure requirements',
          'Present a sketch of key ICT infrastructure components'
        ],
        activities: ['Requirements analysis', 'Infrastructure sketching', 'Planning exercise'],
        resources: ['Infrastructure diagrams', 'Planning templates']
      },
      {
        week: 2,
        unit: 'Unit 4: ICT Infrastructure',
        topic: 'Network Types',
        strand: 'Strand 1: Personal Computer Systems',
        subStrand: '13ICT1.3 - ICT Infrastructure',
        learningOutcomes: [
          'State key features of LAN, WAN, MAN, and WiFi networks',
          'Outline key features of each network type',
          'Explain the choice of appropriate network type for an organisation'
        ],
        activities: ['Network comparison', 'Feature analysis', 'Network selection'],
        resources: ['Network diagrams', 'Feature charts']
      },
      {
        week: 3,
        unit: 'Unit 4: ICT Infrastructure',
        topic: 'Open Source vs Proprietary Software',
        strand: 'Strand 1: Personal Computer Systems',
        subStrand: '13ICT1.3 - ICT Infrastructure',
        learningOutcomes: [
          'Name commonly used open source and proprietary software',
          'State strengths of each software type for local companies',
          'Compare open source and proprietary software'
        ],
        activities: ['Software comparison', 'Strength analysis', 'Selection criteria'],
        resources: ['Software examples', 'Comparison charts']
      },
      {
        week: 4,
        unit: 'Unit 4: ICT Infrastructure',
        topic: 'Software Installation and Management',
        strand: 'Strand 1: Personal Computer Systems',
        subStrand: '13ICT1.3 - ICT Infrastructure',
        learningOutcomes: [
          'Download required open-source software from the Internet',
          'Install and un-install software on a PC',
          'Produce an ICT infrastructure report'
        ],
        activities: ['Software installation', 'Management practice', 'Report writing'],
        resources: ['Software packages', 'Installation guides'],
        assessment: 'Infrastructure Report (15%)'
      },
      // Unit 5: Database Driven Websites (Weeks 5-10)
      {
        week: 5,
        unit: 'Unit 5: Database Driven Websites',
        topic: 'Web Design Requirements',
        strand: 'Strand 2: Application Software',
        subStrand: '13ICT2.2 - Database Driven Websites',
        learningOutcomes: [
          'State key web-design requirements for an organisation',
          'Outline web-design requirements',
          'Explain key requirements for meeting specific needs'
        ],
        activities: ['Requirements gathering', 'Design planning', 'Needs analysis'],
        resources: ['Web design guides', 'Requirements templates']
      },
      {
        week: 6,
        unit: 'Unit 5: Database Driven Websites',
        topic: 'CSS Fundamentals',
        strand: 'Strand 2: Application Software',
        subStrand: '13ICT2.2 - Database Driven Websites',
        learningOutcomes: [
          'Define CSS',
          'Outline the types of CSS styles',
          'State the building blocks of CSS syntax'
        ],
        activities: ['CSS introduction', 'Style creation', 'Syntax practice'],
        resources: ['CSS reference', 'Code editor']
      },
      {
        week: 7,
        unit: 'Unit 5: Database Driven Websites',
        topic: 'HTML and CSS Integration',
        strand: 'Strand 2: Application Software',
        subStrand: '13ICT2.2 - Database Driven Websites',
        learningOutcomes: [
          'Explain the key benefits of using CSS',
          'Describe the link between CSS and HTML',
          'Create an external CSS file with correct rulesets'
        ],
        activities: ['HTML/CSS linking', 'Stylesheet creation', 'Website styling'],
        resources: ['Code editor', 'HTML/CSS guides']
      },
      {
        week: 8,
        unit: 'Unit 5: Database Driven Websites',
        topic: 'Database-Driven Website Concepts',
        strand: 'Strand 2: Application Software',
        subStrand: '13ICT2.2 - Database Driven Websites',
        learningOutcomes: [
          'State required components for database-driven websites',
          'Outline major components involved',
          'Explain the database-driven website concept'
        ],
        activities: ['Component identification', 'Architecture overview', 'Concept exploration'],
        resources: ['Architecture diagrams', 'Database examples']
      },
      {
        week: 9,
        unit: 'Unit 5: Database Driven Websites',
        topic: 'Web Programming Basics',
        strand: 'Strand 2: Application Software',
        subStrand: '13ICT2.2 - Database Driven Websites',
        learningOutcomes: [
          'Identify common web programming tools',
          'Create a database table using database management software',
          'Write basic code using a web programming language'
        ],
        activities: ['Database setup', 'Basic scripting', 'Connection coding'],
        resources: ['Database software', 'Programming environment']
      },
      {
        week: 10,
        unit: 'Unit 5: Database Driven Websites',
        topic: 'Website Project and Validation',
        strand: 'Strand 2: Application Software',
        subStrand: '13ICT2.2 - Database Driven Websites',
        learningOutcomes: [
          'Produce a website with database capabilities',
          'Test that database-driven website returns correct information',
          'Validate a created website for best performance'
        ],
        activities: ['Website completion', 'Testing', 'Validation'],
        resources: ['Web development tools', 'Validation tools'],
        assessment: 'Database-Driven Website Project (20%)'
      }
    ]
  },
  term3: {
    name: 'Term 3',
    focus: 'Data Representation & Programming',
    weeks: 10,
    scheme: [
      // Unit 6: Data Representation (Weeks 1-4)
      {
        week: 1,
        unit: 'Unit 6: Data Representation',
        topic: 'Unicode and Number Systems',
        strand: 'Strand 3: Graphics Manipulation and Programming',
        subStrand: '13ICT3.1 - Data Representation',
        learningOutcomes: [
          'Define Unicode',
          'State the importance of Unicode',
          'Convert a string of binary digits to hexadecimal'
        ],
        activities: ['Unicode introduction', 'Number system basics', 'Conversion practice'],
        resources: ['Number system charts', 'Conversion guides']
      },
      {
        week: 2,
        unit: 'Unit 6: Data Representation',
        topic: 'Number Conversions',
        strand: 'Strand 3: Graphics Manipulation and Programming',
        subStrand: '13ICT3.1 - Data Representation',
        learningOutcomes: [
          'Convert between decimals, binary and hexadecimal',
          'Perform arithmetic operations in binary',
          'Use 2\'s complement for subtraction'
        ],
        activities: ['Conversion exercises', 'Arithmetic practice', 'Problem solving'],
        resources: ['Conversion tables', 'Practice worksheets']
      },
      {
        week: 3,
        unit: 'Unit 6: Data Representation',
        topic: 'Logic Gates and Truth Tables',
        strand: 'Strand 3: Graphics Manipulation and Programming',
        subStrand: '13ICT3.1 - Data Representation',
        learningOutcomes: [
          'Construct a truth table for a single gate (AND, OR, NOT)',
          'Construct truth tables for multi-input logic circuits',
          'Understand Boolean operations'
        ],
        activities: ['Truth table construction', 'Gate operation', 'Boolean logic'],
        resources: ['Logic gate diagrams', 'Truth table templates']
      },
      {
        week: 4,
        unit: 'Unit 6: Data Representation',
        topic: 'Logic Circuits',
        strand: 'Strand 3: Graphics Manipulation and Programming',
        subStrand: '13ICT3.1 - Data Representation',
        learningOutcomes: [
          'Construct an electrical circuit using logic gates',
          'Construct a logic circuit for a given truth table',
          'Complete data representation assessment'
        ],
        activities: ['Circuit construction', 'Circuit design', 'Assessment'],
        resources: ['Circuit components', 'Assessment materials'],
        assessment: 'Data Representation Test (15%)'
      },
      // Unit 7: Programming II (Weeks 5-10)
      {
        week: 5,
        unit: 'Unit 7: Programming II',
        topic: 'Stages of Programming',
        strand: 'Strand 3: Graphics Manipulation and Programming',
        subStrand: '13ICT3.2 - Programming II',
        learningOutcomes: [
          'State the main purpose of programming',
          'List the stages of programming',
          'Describe the function of each programming stage'
        ],
        activities: ['Programming overview', 'Stage exploration', 'Process mapping'],
        resources: ['Programming guides', 'Stage diagrams']
      },
      {
        week: 6,
        unit: 'Unit 7: Programming II',
        topic: 'Top-Down and Bottom-Up Design',
        strand: 'Strand 3: Graphics Manipulation and Programming',
        subStrand: '13ICT3.2 - Programming II',
        learningOutcomes: [
          'Identify top-down and bottom-up design tools',
          'State benefits of each approach',
          'Explain why these approaches are beneficial'
        ],
        activities: ['Design approach comparison', 'Tool identification', 'Benefit analysis'],
        resources: ['Design methodology guides', 'Comparison charts']
      },
      {
        week: 7,
        unit: 'Unit 7: Programming II',
        topic: 'Pseudocode and Flowcharts',
        strand: 'Strand 3: Graphics Manipulation and Programming',
        subStrand: '13ICT3.2 - Programming II',
        learningOutcomes: [
          'Present solution logic in Pseudocode or algorithm',
          'Present program flow using flow diagrams',
          'Design complete program solutions'
        ],
        activities: ['Pseudocode writing', 'Flowchart creation', 'Solution design'],
        resources: ['Flowchart templates', 'Pseudocode examples']
      },
      {
        week: 8,
        unit: 'Unit 7: Programming II',
        topic: 'Coding Practices',
        strand: 'Strand 3: Graphics Manipulation and Programming',
        subStrand: '13ICT3.2 - Programming II',
        learningOutcomes: [
          'Define coding',
          'Apply correct elements of coding to a program',
          'Define modular programming'
        ],
        activities: ['Code writing', 'Best practices', 'Modular design'],
        resources: ['Programming environment', 'Code standards']
      },
      {
        week: 9,
        unit: 'Unit 7: Programming II',
        topic: 'Modular Programming',
        strand: 'Strand 3: Graphics Manipulation and Programming',
        subStrand: '13ICT3.2 - Programming II',
        learningOutcomes: [
          'State benefits of modular programming',
          'Produce a program following modular concepts',
          'Create functions and modules'
        ],
        activities: ['Module creation', 'Function writing', 'Program structure'],
        resources: ['Programming environment', 'Module examples']
      },
      {
        week: 10,
        unit: 'Unit 7: Programming II',
        topic: 'Program Testing and Project',
        strand: 'Strand 3: Graphics Manipulation and Programming',
        subStrand: '13ICT3.2 - Programming II',
        learningOutcomes: [
          'State the purpose of testing a program',
          'Outline steps used in testing a program',
          'Produce a working program with modules'
        ],
        activities: ['Program testing', 'Debugging', 'Project completion'],
        resources: ['Testing tools', 'Project requirements'],
        assessment: 'Programming Project (20%)'
      }
    ]
  },
  term4: {
    name: 'Term 4',
    focus: 'Embedded Systems & ICT Issues',
    weeks: 10,
    scheme: [
      // Unit 8: Embedded Microprocessors (Weeks 1-4)
      {
        week: 1,
        unit: 'Unit 8: Embedded Microprocessors',
        topic: 'Microprocessor Components',
        strand: 'Strand 3: Graphics Manipulation and Programming',
        subStrand: '13ICT3.3 - Embedded Microprocessors',
        learningOutcomes: [
          'Name major components of a microprocessor',
          'Outline major microprocessor components',
          'State links within microprocessor components'
        ],
        activities: ['Component identification', 'Architecture study', 'Interaction analysis'],
        resources: ['Microprocessor diagrams', 'Component charts']
      },
      {
        week: 2,
        unit: 'Unit 8: Embedded Microprocessors',
        topic: 'High-Level to Machine Code',
        strand: 'Strand 3: Graphics Manipulation and Programming',
        subStrand: '13ICT3.3 - Embedded Microprocessors',
        learningOutcomes: [
          'State major steps in changing high-level code to machine code',
          'Describe the compilation process',
          'Explain how programs are converted to machine code'
        ],
        activities: ['Compilation overview', 'Process tracing', 'Code transformation'],
        resources: ['Compilation diagrams', 'Process guides']
      },
      {
        week: 3,
        unit: 'Unit 8: Embedded Microprocessors',
        topic: 'Programming Microprocessors',
        strand: 'Strand 3: Graphics Manipulation and Programming',
        subStrand: '13ICT3.3 - Embedded Microprocessors',
        learningOutcomes: [
          'Present simple instructions to control a microprocessor',
          'Present mnemonic code to instruct a microprocessor',
          'Present high-level code for microprocessor control'
        ],
        activities: ['Instruction writing', 'Mnemonic coding', 'High-level programming'],
        resources: ['Microprocessor kits', 'Instruction references']
      },
      {
        week: 4,
        unit: 'Unit 8: Embedded Microprocessors',
        topic: 'Embedded Devices',
        strand: 'Strand 3: Graphics Manipulation and Programming',
        subStrand: '13ICT3.3 - Embedded Microprocessors',
        learningOutcomes: [
          'Define embedded microprocessor and embedded device',
          'State situations where microprocessors are embedded',
          'Explain the importance of error-free software for embedded devices'
        ],
        activities: ['Embedded systems exploration', 'Project work', 'Assessment'],
        resources: ['Embedded examples', 'Project requirements'],
        assessment: 'Embedded Systems Project (15%)'
      },
      // Unit 9: Safety and Security in ICT II (Weeks 5-7)
      {
        week: 5,
        unit: 'Unit 9: Safety and Security in ICT II',
        topic: 'Cyber Security Attacks and Threats',
        strand: 'Strand 4: Communication and Network Systems',
        subStrand: '13ICT4.1 - Safety and Security in ICT II',
        learningOutcomes: [
          'State common types of Cyber security attacks',
          'State common Cyber security threats',
          'Outline effective measures to prevent attacks'
        ],
        activities: ['Threat identification', 'Attack analysis', 'Prevention strategies'],
        resources: ['Security materials', 'Case studies']
      },
      {
        week: 6,
        unit: 'Unit 9: Safety and Security in ICT II',
        topic: 'Threat Sources and Effects',
        strand: 'Strand 4: Communication and Network Systems',
        subStrand: '13ICT4.1 - Safety and Security in ICT II',
        learningOutcomes: [
          'State threats posed by hackers, crackers, employees, terrorists',
          'Outline effects of cyber-attacks or threats',
          'Explain effects of viruses and malware'
        ],
        activities: ['Threat source analysis', 'Impact assessment', 'Virus study'],
        resources: ['Security reports', 'Impact case studies']
      },
      {
        week: 7,
        unit: 'Unit 9: Safety and Security in ICT II',
        topic: 'Online Identity and Guidelines',
        strand: 'Strand 4: Communication and Network Systems',
        subStrand: '13ICT4.1 - Safety and Security in ICT II',
        learningOutcomes: [
          'State issues with having an online identity',
          'Explain issues with online identity in social media',
          'Outline key Cyber security guidelines'
        ],
        activities: ['Online identity discussion', 'Guideline review', 'Report writing'],
        resources: ['Security guidelines', 'Report templates'],
        assessment: 'Cyber Security Report (10%)'
      },
      // Unit 10: Ethical & Sustainable Measures in ICT (Weeks 8-10)
      {
        week: 8,
        unit: 'Unit 10: Ethical & Sustainable Measures in ICT',
        topic: 'Cyber-crime and Legislation',
        strand: 'Strand 4: Communication and Network Systems',
        subStrand: '13ICT4.2 - Ethical Measures in ICT',
        learningOutcomes: [
          'Define Cyber-crime and Cyber legislation',
          'State growing Cyber-crime concerns',
          'Outline main components of known Cyber legislation'
        ],
        activities: ['Legislation study', 'Crime analysis', 'Legal framework'],
        resources: ['Legal materials', 'Legislation documents']
      },
      {
        week: 9,
        unit: 'Unit 10: Ethical & Sustainable Measures in ICT',
        topic: 'ICT Waste and Environmental Impact',
        strand: 'Strand 4: Communication and Network Systems',
        subStrand: '13ICT4.3 - Sustainable Measures in ICT',
        learningOutcomes: [
          'State physical waste problems that ICT creates',
          'Outline environmental concerns with ICT waste',
          'Explain impacts of ICT electricity usage'
        ],
        activities: ['Waste analysis', 'Environmental impact', 'Sustainability discussion'],
        resources: ['Environmental reports', 'Sustainability guides']
      },
      {
        week: 10,
        unit: 'Unit 10: Ethical & Sustainable Measures in ICT',
        topic: 'Sustainable Technology and Final Assessment',
        strand: 'Strand 4: Communication and Network Systems',
        subStrand: '13ICT4.3 - Sustainable Measures in ICT',
        learningOutcomes: [
          'Define sustainable technology',
          'State effective measures for sustainable ICT industry',
          'Produce sustainability plan'
        ],
        activities: ['Sustainability planning', 'Final assessment', 'Year review'],
        resources: ['Sustainability materials', 'Assessment papers'],
        assessment: 'Sustainability Plan (10%)'
      }
    ]
  }
};

// Export default
export default {
  year11ICTWeeklyLessons,
  year12ICTWeeklyLessons,
  year13ICTWeeklyLessons
};
